import "./styles.css";

import type { DaylightPreview } from "./dev/daynight";
import type { DebugLog, DebugReading, LocalWatch } from "./dev/debuglog";
import type { DebugPanel } from "./dev/debugpanel";
import type { Flight } from "./dev/flight";
import type { GiveDoor } from "./dev/give";
import type { DebugMarker } from "./dev/handle";
import type { TrackMarker } from "./dev/trackmarker";
import { downloadText } from "./host/download";
import { FastForward, type FastForwardHost, runStepped } from "./host/fastforward";
import { Rest, type RestWorld } from "./host/rest";
import { HostLoop } from "./host/loop";
import { TICK_SECONDS } from "./host/timestep";
import { Controls } from "./input/controls";
import { TouchControls, touchDeviceSaysYes, touchMode } from "./input/touch";
import {
  campPrompt,
  crossingLine,
  REFUSED_ALREADY_CROSSING,
  restPrompt,
  waitPrompt,
} from "./hud/crossing";
import { Journal, formatWorldDate } from "./journal/journal";
import { People } from "./journal/people";
import { type CameraPose, CRAWL_INTENT_SCALE, PlayerController } from "./player/controller";
import { standingRoomNear } from "./player/standing";
import { DIG_RANGE_TILES, type RayHit, rayFromPose, raycastVoxels } from "./player/raycast";
import { ChunkMeshRegistry } from "./render/chunkMeshes";
import { isNight, MORNING_PHASE, type TimeOfDaySource } from "./render/daylight";
import { ArrowFlight } from "./render/arrows";
import { TileHighlight } from "./render/highlight";
import { SceneView } from "./render/scene";
import { Hud } from "./hud/hud";
import { openTheBook } from "./hud/opening";
import { type BornSession, chooseAnOrigin } from "./hud/origins";
import { SaveButton } from "./hud/panels";
import {
  showTheTitle,
  storedTouchChoice,
  titleRequested,
  touchChoiceSaysYes,
} from "./hud/title";
import { markTitleFrame } from "./session/boot";
import { debugAtBoot, devRequested, homeRequested, resolveHome } from "./session/params";
import { refusedLine, restoredLine, restoringLine } from "./session/restore";
import { applySessionWorld } from "./session/world";
import { surfaceZ } from "./witness/crowd";
import { directorKnobs } from "./witness/director";
import {
  type BirthDoor,
  EMPTY_ORIGINS,
  type OriginsOffer,
  type OriginsReading,
  readOrigins,
} from "./witness/origins";
import { applySalienceDecay } from "./witness/saga";
import { Witnessing } from "./witness/witness";
import { INTEREST_RADIUS_CHUNKS } from "./world/streaming";
import {
  chunkOfTile,
  type EventId,
  NOBODY_RESTING,
  type SettlementId,
  type WitnessSource,
  type WorldStats,
} from "./world/types";
import {
  createWasmWorld,
  loadTheSimulation,
  type RestoredWorld,
  restoreWasmWorld,
} from "./world/wasm";

/*
 * Wiring, and nothing else.
 *
 * The shape of a frame is the whole content of this file:
 *
 *   beginFrame  — consume the mouse, turn the view, honour a file request
 *   step * n    — move the player, dig, tell the world where he is, tick the world
 *   render      — upload whatever meshes arrived, draw at the interpolated pose
 *
 * Wave 1B built all of this against a stub `WorldSource` and predicted the swap
 * would cost one line. It cost three: the construction below is `await
 * createWasmWorld()` instead of `new StubWorldSource()`, and the spawn point and
 * view distance now come back with it — because where the ground is, is a
 * question only the simulation can answer (`world/wasm.ts`). Everything after
 * that line is untouched from the stub era, which is the result the seam was for
 * (docs/plans/phase-1-plan.md, decision 6).
 *
 * Wave 5A added a fourth thing to that construction and did not change the shape
 * of a frame at all: `witness` comes back beside the world, and the same
 * `Witnessing` the fixture drove since 1B is handed the live one instead
 * (docs/plans/phase-3-plan.md, wave 5A). What the two waves between them cost
 * this file is the paragraph below about which source wins, and the loading
 * state — because a world with two centuries of history behind it takes a couple
 * of seconds to build and a blank page is not a loading screen.
 */

const PLAY_PROMPT =
  "click to play\n\nWASD to walk · hold shift to run · mouse or arrow keys to look · space to jump" +
  " · click or E to dig";

/**
 * The same invitation for a session with no keyboard and no pointer lock
 * (`input/touch.ts`, `docs/decisions/input.md`).
 *
 * **The owner's report of 2026-08-29 is the whole reason this string exists**:
 * *"the screen with instructions I cannot click"*. On a phone the overlay above
 * asked for a click that never came — the pointer lock cannot be taken on a
 * touchscreen, so the one gesture the prompt named was the one gesture that
 * could not be made, and the game was unstartable behind a wall of text about a
 * keyboard.
 *
 * It names the two halves of the screen rather than any control, because there
 * is nothing drawn to name: the sticks are wherever the thumb lands.
 */
const TOUCH_PROMPT =
  "tap to play\n\ndrag the left of the screen to walk · drag the right to look" +
  "\npush your thumb to the edge of the ring to run";

/**
 * The extra line the prompt gets when there is somebody to talk to.
 *
 * Appended rather than baked in, because F and J are bound whether or not a
 * witnessing source exists and a prompt that advertised a verb doing nothing
 * would be a worse lie than a prompt that stayed quiet. A session with
 * `?bootstrap=0` is exactly that case: no history, no towns, nobody to meet, and
 * the Phase 1 dig prototype as it was (`world/wasm.ts`).
 */
const WITNESS_PROMPT =
  "\nF talks to whoever is in front of you, and picks up what is at your feet" +
  "\nG takes a piece of the town's work · Q puts down what you are carrying" +
  "\nI opens your hands · P puts armour on · O takes it off" +
  "\nR looses an arrow at whoever you are aiming at · J opens the journal" +
  "\nM opens the map of the ground you have walked" +
  "\nX waits until morning · C makes camp until morning, once it is dark" +
  "\nB rests until you are fit, once something has hurt you";

/**
 * The rest of the prompt: how to stop, and how to hand a session back.
 *
 * After the verbs rather than among them, because that is the order somebody
 * reads them in — what to press, then what to do when they are done. The save
 * line is the one piece of *asking* in the whole client and it is on the surface
 * a player meets when they let go of the mouse, which is when they would think
 * about it (Phase 4 decision 7).
 */
const SESSION_PROMPT =
  "\nesc releases the mouse · the keyboard works without it\n" +
  "“save your session” in the corner writes the replay file (F9 does the same)";

/**
 * The touch twin of {@link WITNESS_PROMPT}: the same verbs, named by the button
 * that fires them rather than by a key nobody can press.
 *
 * One line per row of the pad, which is how they are reached
 * (`input/touch.ts`'s `TOUCH_BUTTONS`).
 */
const TOUCH_WITNESS_PROMPT =
  "\nuse talks to whoever is in front of you, and picks up what is at your feet" +
  "\ndig and gather take what is under the crosshair" +
  "\n··· opens the menu, camp, waiting, resting and drop";

/**
 * And the touch twin of {@link SESSION_PROMPT}.
 *
 * There is no escape to name — nothing was captured, so nothing is released
 * (`input/controls.ts`'s `releaseLock`) — so what is left to say is the one
 * piece of *asking* the client does, which moved under the ⋯ because the corner
 * it used to sit in is where a thumb walks now.
 */
const TOUCH_SESSION_PROMPT =
  "\n“save” under ··· writes the replay file — the whole of what a playtest hands back";

/**
 * The first-run card: the four verbs, and not one word about the world.
 *
 * The roadmap's "no tutorial beyond keys" and Phase 4 decision 7's list,
 * verbatim — move, look, talk, journal. It is shown once, dismissed the first
 * time the player does anything (`input/controls.ts`'s `hasPlayed`), and never
 * comes back. Everything else a session needs to say lives on the click-to-play
 * overlay above, which is a different surface with a different lifetime: that
 * one returns every time the pointer is released, and this one is gone.
 *
 * The dig verb is deliberately absent. It is on the overlay, because it is still
 * bound and a player who reads the overlay should find it — but the phase's cut
 * list has it pre-authorised for removal if the owner's dry-run says it
 * distracts, and the four verbs on this card are the ones the playtest's
 * questions are about.
 *
 * Camping joins it, and the card grew a word rather than a line: the phase's
 * day is twenty minutes long and a player who does not know night can be slept
 * through will conclude the game has none (Phase 5 decision 8). "When it is
 * dark" is the whole of the tutorial it gets — the verb says nothing until the
 * sky has made it available, which is the rest of the teaching.
 *
 * The map joins it the same way and for the same kind of reason (decision 10):
 * a map that draws only walked ground is invisible to a player who never
 * presses the key, and unlike every other verb here there is nothing in the
 * world to walk up to that would suggest it. One letter, no explanation — what
 * it *contains* is the thing the playtest is asking about.
 */
const KEY_CARD = [
  "WASD move · mouse look",
  "F talk, read, pick up · G gather · Q drop · I hands · P on · O off",
  "mouse dig or swing · R loose",
  "Tab menu · J journal · M map · X wait until morning · C camp when it is dark",
  // Its own row rather than a fourth clause on the one above, because it is the
  // only verb on this card whose availability is a fact about the player's body:
  // the other nine can be pressed at any moment of any session and this one
  // answers something that has happened to them (chunk Fx-5).
  "B rest until you are fit, when something has hurt you",
];

/**
 * The same card for a session played with thumbs.
 *
 * Three lines like the card above and the same four verbs the playtest's
 * questions are about — it is the *card* that is Phase 4 decision 7's, not the
 * letters on it. What is named here is where a finger goes, because on a
 * touchscreen that is what a binding is.
 */
const TOUCH_CARD = [
  "left thumb walks · right thumb looks",
  "use talks, reads and picks up · jump · dig · gather",
  "··· opens the menu, camp, waiting and rest",
];

/** What the save button says, and what it says about itself on hover. */
const SAVE_LABEL = "save your session";
const SAVE_HINT =
  "downloads a replay file of everything this session did — the whole of what a" +
  " playtest hands back (nothing is sent anywhere)";

/**
 * What the page says while the simulation is being built.
 *
 * **The default session spends a couple of seconds inside `new WasmSim`**,
 * building the curated world's history before the first frame
 * (`session/world.ts`'s `SESSION_WORLD`; `world/wasm.ts` has the measured table
 * of what a century costs). That work is synchronous and single-threaded — it
 * has to be, it is the simulation — so nothing can be drawn during it and the
 * only honest thing to do is say what is happening *before* it starts. The
 * overlay is written and the module load is awaited on the next line, which is
 * what gives the browser the frame it needs to paint it.
 *
 * It says what it is waiting for rather than "loading": a wait somebody
 * understands is a wait they will sit through, and this one is a feature of the
 * world rather than a download.
 */
const LOADING_PROMPT = "building the world\n\nits history is simulated before you arrive in it";

const canvas = document.querySelector<HTMLCanvasElement>("#viewport");
const hudRoot = document.querySelector<HTMLElement>("#hud");

if (canvas === null || hudRoot === null) {
  throw new Error("index.html must provide #viewport and #hud");
}

/*
 * Which world this session is (`session/world.ts`, Phase 4 decision 7).
 *
 * First, before anything reads a URL parameter, because everything that decides
 * what gets built reads one: the seed and the bootstrap in `world/wasm.ts`, the
 * spawn town in `session/params.ts`. What it does is fill in whatever the link
 * did not name from the one baked-in constant, and put the result in the address
 * bar — so the default link plays the curated world, and the link a playtester
 * copies afterwards names it in full.
 */
applySessionWorld();

/**
 * Whether the development surfaces were asked for: `?dev=1`.
 *
 * Two gates, not one. `import.meta.env.DEV` keeps the trace view's wiring, the
 * self-checks, the fixture and the debug handle out of a production bundle
 * altogether; this keeps them out of a *development* session that did not ask,
 * which is the case devlog #10's postscript found — the owner's dry-run and a
 * playtester's first five minutes run the same build, and a self-check verdict
 * across the HUD is a bug report nobody asked for (Phase 4 decision 7).
 */
const dev = import.meta.env.DEV && devRequested(window.location.search);

/**
 * Whether this session is played with fingers (`input/touch.ts`).
 *
 * Read here, once, before anything that depends on it exists: the key card is
 * built with the HUD a few lines below and says what a player should press,
 * the witnessing layer's aiming prompt names a key or does not, and `Controls`
 * has to know whether there is a pointer lock to ask for at all. Three surfaces,
 * one fact, decided in the boot sequence — the same shape `dev` is read in
 * above.
 *
 * The link wins where it says anything (`?touch=1`, `?touch=0`), then the
 * setting the title screen remembers (`hud/title.ts`, chunk U-6), then the
 * device — which is the one piece of this that is not a URL parameter: it is a
 * fact about what somebody is holding (`docs/decisions/input.md`).
 *
 * The setting is read *here*, in the boot, and nowhere else — which is why the
 * settings page says a change is in force from the next session rather than
 * pretending to re-dress this one: three of the five surfaces this boolean
 * reaches are built above the title screen.
 */
const device = touchDeviceSaysYes();
const touch = touchMode(
  window.location.search,
  device.coarsePointer,
  device.touchPoints,
  touchChoiceSaysYes(storedTouchChoice()),
);

// And once on the document, where the ROOT SCALE can read it: a phone in
// landscape falls through the bottom of `1.4815vh` and would wear the interface
// at 0.44 of desktop, so a touch session gets its own floor (`ui/base.css`,
// chunk U-0). Said here rather than in `input/touch.ts` because it is true of
// the page before any control is built, and it stays true if a session is one
// that draws no pad at all.
document.documentElement.classList.toggle("touch", touch);

/**
 * Whether there is a session to export yet.
 *
 * The save button is built with the HUD, which is built before the world so that
 * the page can say what it is waiting for — so for the couple of seconds the
 * bootstrap takes, the button is on screen and there is nothing behind it. It
 * says so rather than throwing.
 */
let sessionReady = false;

// Before the world, so there is something on the page while it is built. The HUD
// is plain DOM and needs nothing from the simulation to exist; only its readout
// does, and that starts once there are numbers to put in it.
const hud = new Hud(
  hudRoot,
  {
    keys: touch ? TOUCH_CARD : KEY_CARD,
    saveLabel: SAVE_LABEL,
    saveHint: SAVE_HINT,
    onSave: () => {
      if (!sessionReady) {
        hud.setNotice("the world is still being built — there is no session to save yet");

        return;
      }

      exportSession();
    },
  },
  // And whether the panels' own hints name a key or the button that closes
  // them (`hud/hud.ts`). The third thing this one boolean reaches, after the
  // key card above and the aiming prompt below.
  touch,
);

/*
 * ------------------------- The first frame -------------------------
 *
 * The title screen, before any world work at all (P-8, requirement T-1). The
 * owner's finding is the whole of the reason it moved: *"seems like the world is
 * being generated before the main menu … generation of world and everything like
 * that doesn't happen until you click begin"*.
 *
 * Chunk U-6 put it after the world on the opposite argument — that a title
 * answered before the wasm arrived would have a two-second pause behind *begin*
 * — and the argument was wrong about which wait a player minds. The pause behind
 * *begin* is the world being built and the loading line says so; the pause
 * *before* the title is a game that has not started yet, with nothing on the
 * screen to say why. A menu that appears instantly and then works for two
 * seconds is the shape every game with a world has.
 *
 * What may still load while the screen is up is the **module** and nothing else:
 * 630 KiB of wasm that decides nothing, fetched and compiled on the way
 * (`world/wasm.ts`'s `loadTheSimulation`). No seed is read, no world is
 * constructed and no history is simulated until this promise resolves — which is
 * the difference the finding is about.
 *
 * It also runs **before the controls exist**, which is the reason U-6 gave and
 * is still true: the keys that walk the rail cannot collide with the keys that
 * walk (`hud/opening.ts` says the same about Enter and Escape).
 *
 * The mourning flow deliberately does **not** come back through it: a death
 * re-enters at the lives, which is the ruling `mourn()` is written under (the
 * epitaph is the strip's lead, chunk U-4). A title screen between a killing and
 * the heir would be the session ending rather than the life.
 */

// On its way while the screen is up, and awaited by whichever door builds a
// world. Nothing here decides anything: a session that never presses *begin*
// has fetched a module and built nothing.
void loadTheSimulation().catch(() => {
  // Swallowed here and nowhere else: the same promise is awaited behind both
  // doors, where a module that would not load is a wait that fails in front of
  // somebody who asked for a world.
});

/** Whether a world has been built, for the check that this order is the order. */
let worldStanding = false;

/** The session a file was restored into, or `null` for a world built from a seed. */
let loaded: RestoredWorld | null = null;

// Written down as it happens, because an order cannot be checked afterwards:
// the self-checks run when everything exists (`session/boot.ts`).
markTitleFrame(titleRequested(window.location.search), worldStanding);

await showTheTitle(hudRoot, window.location.search, touch, {
  /*
   * The load door (P-8, requirement T-2; decision D-5).
   *
   * Everything about *what a file is* is behind this call — the format, the
   * refusals and the re-run are the simulation's (`world/wasm.ts`,
   * `crates/sim-wasm`'s `record`) — and everything about *saying so* is here:
   * the loading line counts the ticks as they are replayed, and a refused file
   * comes back as a sentence the rail draws under itself.
   *
   * The prompt is written and a frame is let by before the first batch, for the
   * reason the loading line below is: a callback runs before the paint it was
   * scheduled for, and a restore that never yielded would be a page that stopped
   * answering with nothing on it.
   */
  load: async (text: string): Promise<string | null> => {
    const outcome = await restoreWasmWorld(text, async (progress) => {
      hud.setOverlay(restoringLine(progress.done, progress.total));
      await painted();
    });

    if ("refused" in outcome) {
      hud.setOverlay(null);

      return refusedLine(outcome.refused);
    }

    loaded = outcome;

    return null;
  },
});

/**
 * The file this session came out of, or `null`. Read once, after the screen.
 *
 * Through a function rather than off the variable, and the reason is the type
 * checker rather than taste: `loaded` is assigned inside the door above, which
 * is a callback, and control-flow narrowing does not follow one — read directly
 * it is still the `null` it was initialised as, and every use of the world below
 * would be a use of `never`.
 */
const restored = loadedSession();

if (restored === null) {
  hud.setOverlay(LOADING_PROMPT);

  // Two frames, so the message is *painted* and not merely written: a callback
  // runs before the paint it was scheduled for, so the second one is the first
  // moment the pixels are known to exist. Thirty milliseconds against a
  // two-second wait, and the difference between a loading state that always
  // shows and one that shows whenever the wasm fetch happens to be slow enough.
  await painted();
}

const buildStarted = performance.now();

// The simulation. Everything asynchronous in this client is behind this call or
// behind the restore above: instantiating a wasm module is a promise, and
// pretending otherwise would put a loading state into every consumer of the
// world.
const { world, witness, spawn, viewDistance, walkTicksPerStep } =
  restored ?? (await createWasmWorld());

// Written after the world and read *above*, at the title: a boot that built one
// first would mark the frame with this already true, and the check that the
// order is this order would fail rather than a screenshot noticing
// (`session/boot.ts`).
worldStanding = true;

// A restored session's wait happened while the title was up, so the number the
// play prompt shows is the one the load itself measured — bootstrap and re-run
// together — rather than the microseconds since the screen went down.
const buildSeconds = restored?.restored.seconds ?? (performance.now() - buildStarted) / 1000;

const player = new PlayerController(world.terrain, spawn);
const view = new SceneView(canvas, viewDistance);
const chunks = new ChunkMeshRegistry(view.scene);
const highlight = new TileHighlight(view.scene);
// The drawn shaft, one for the session and moved rather than rebuilt — the
// highlight's own shape, for its reason. **Nothing about it decides anything**:
// it flies to a body the simulation has already been asked to shoot at
// (`render/arrows.ts`).
const arrows = new ArrowFlight(view.scene);
const journal = new Journal();

// What the player learned, and who they met. Two stores rather than one,
// because they answer different questions and the second is Phase 4's
// (`journal/people.ts`): a journal entry is a record with provenance, and a
// meeting is a person with a home the war can reach.
const people = new People();

// The player's tile before anything reads it: the interest command carries it,
// and `WitnessSource.here` resolves the visited settlement from that command's
// centre (`world/wasm.ts`). Submitted here rather than after the witnessing is
// built so the crowd is anchored around the town the player is standing in
// rather than around the nearest one to the origin — the same town today,
// different ones the moment a session starts anywhere else. It is also interest
// before the first tick, which is what makes the ground exist by the time the
// player has finished falling the first millimetre onto it (plan decision 1: the
// world generates inside the tick).
world.setInterest(player.tile, INTEREST_RADIUS_CHUNKS);

/*
 * The witnessing half of the client: bodies, aftermath props, the interact verb
 * and the journal (`witness/witness.ts`).
 *
 * The live source is what a session gets — `WasmWitnessSource` over the real
 * history tier, built beside the world (`world/wasm.ts`). It is `null` in a
 * world with no past (`?bootstrap=0`), where there are no towns and nobody to
 * meet, and the client is the Phase 1 dig prototype exactly as it was.
 *
 * The hand-authored fixture stays as the dev-flag fallback and *overrides* the
 * live source when asked for (`?fixture=1`), because a visual that needs a
 * particular war two towns away is faster to judge against data somebody chose
 * than against a seed somebody has to find (`world/fixture.ts`). Both halves of
 * its guard still matter: `import.meta.env.DEV` keeps the fake data out of the
 * production bundle entirely, and the URL parameter keeps an ordinary session
 * live.
 */
let witnessSource: WitnessSource | null = witness;
// Whether what the session is reading is the hand-authored fixture rather than a
// world. Only one thing downstream cares — the decay knob, which has nothing to
// fade there — and it is recorded here because this is the only place that knows.
let authoredSource = false;

if (import.meta.env.DEV) {
  const { createFixtureWitness, fixtureRequested } = await import("./world/fixture");

  if (fixtureRequested(window.location.search)) {
    witnessSource = createFixtureWitness({
      origin: player.tile,
      // The fixture has no world of its own, so it borrows the client's probe
      // to stand its props on the ground (`world/fixture.ts`).
      groundAt: (x: number, y: number) => surfaceZ(world.terrain, x, y),
    });
    authoredSource = true;
  }
}

/**
 * Where a birth is queued: the world, or the counter behind `?bornfake=1`.
 *
 * `WorldSource` satisfies the handle structurally, so nothing is wrapped and
 * nothing new crosses the seam — the same bargain the Phase 5 deeds are handed
 * over under (`witness/origins.ts`'s `BirthDoor`). A build whose boundary has not
 * grown the door yet satisfies it too, with every member absent, which is exactly
 * what the screen checks before it opens.
 *
 * **This assignment is the cross-chunk signature check.** The three verbs are
 * wave 3B's, and the day their spellings stop matching the handle
 * `witness/origins.ts` reads through — a renamed verb, a reordered pair of
 * handles — is the day this line stops compiling. That is the whole of what the
 * compiler can do for a seam whose other half is a *runtime* presence check, and
 * it is worth one line to have it.
 */
let birthDoor: BirthDoor = world;

/**
 * What the birth screen reads its lives out of: the session's world, or the
 * hand-authored stand-in behind `?bornfake=1`.
 *
 * Two reads rather than a whole source (`witness/origins.ts`'s
 * `OriginsReading`), which is what lets the stand-in sit *beside* the world
 * rather than wrap it: an ordinary session's witnessing layer never learns that
 * this flag exists.
 */
let birthLives: OriginsReading | null = witnessSource;

if (import.meta.env.DEV && witnessSource !== null) {
  /*
   * `?bornfake=1` — the birth screen over twelve lives nobody lived
   * (`dev/birthfake.ts`).
   *
   * Both gates, like the fixture: the fake data never reaches a production
   * bundle, and a development session that did not ask never sees it. It exists
   * because 3A's projection and 3B's exports land *beside* this chunk rather than
   * before it, and a screen that cannot be looked at cannot be judged — the same
   * sentence `world/fixture.ts` was written under, and the same lifetime.
   *
   * What is said out loud is that nothing is being recorded, which is the honesty
   * rule `?home=` and `?decay=` are both under: a session quietly running on
   * something other than what the link named is an unreproducible playtest.
   */
  const { bornFakeRequested, BORN_FAKE_NOTICE, createBirthFake } = await import(
    "./dev/birthfake"
  );

  if (bornFakeRequested(window.location.search)) {
    const fake = createBirthFake(witnessSource);

    birthLives = fake.reading;
    birthDoor = fake.door;
    hud.setNotice(BORN_FAKE_NOTICE);
  }
}

/*
 * `?home=` — which settlement the session starts in (Phase 4 decision 7).
 *
 * Applied here, after the source is chosen and before the witnessing is built,
 * because those are the two constraints: the settlements come off whichever
 * source this session is talking to (so the flag works for the fixture and the
 * live world alike, and the fixture's own towns are the ones it names), and the
 * crowd is anchored around the town the player is standing in the moment
 * `Witnessing` is constructed.
 *
 * **This is presentation, not a simulation input.** The player is not a sim
 * entity (Phase 3 decision 10) and the world is built before this line runs —
 * the same seed, the same two centuries, the same records. What moves is the
 * client's camera and the interest command that follows it, which is why the
 * spawn point is a URL parameter rather than a world parameter.
 */
const home = homeRequested(window.location.search);

/**
 * The town this session opened in, for the claim to seat its figure in.
 *
 * The boot sequence's own answer and the only one available at the instant it is
 * needed: the name is claimed before the first tick and a town promotes inside
 * one, so `here()` is *nowhere* until after the claim would have been sent
 * (`witness/witness.ts`'s `WitnessOptions.home`, item 6's rider 4). The seed
 * chose a town to stand the player in and `?home=` may have chosen another; this
 * is whichever of those actually happened.
 */
let spawnTown: SettlementId | null = spawn.settlement;

if (home !== null && witnessSource !== null) {
  const wanted = resolveHome(home, witnessSource);

  if (wanted === null) {
    // Named nowhere: said out loud rather than ignored, because a session that
    // quietly started somewhere else than the link asked for is an
    // unreproducible playtest (`world/wasm.ts` makes the same call about a
    // malformed seed, one step louder).
    hud.setNotice(`?home=${home} names no settlement in this world — starting where the seed did`);
  } else {
    const z = surfaceZ(world.terrain, wanted.x + 0.5, wanted.y + 0.5);
    player.placeAt({ x: wanted.x + 0.5, y: wanted.y + 0.5, z }, 0, 0);
    spawnTown = wanted.settlement;
    // The world hears about it the same way it hears about a step: the interest
    // command carries the player's tile, and the *simulation* promotes whichever
    // town that square covers (`world/wasm.ts`). Promotion happens inside the
    // first tick, so `here()` is still nowhere for the rest of this boot
    // sequence and the first promoted read arrives on the first step as an
    // ordinary arrival — which is why the claim below carries `spawnTown`
    // instead.
    world.setInterest(player.tile, INTEREST_RADIUS_CHUNKS);
  }
}

/*
 * The director's knobs (`witness/director.ts`, Phase 5 decision 9).
 *
 * Read once, here, and handed to the witnessing layer for the life of the
 * session. Everything about the gate is in `directorKnobs`: a link without
 * `?dev=1` gets the shipped pacing whatever else it says, and anything the
 * parameter asked for that this build will not do is said out loud rather than
 * ignored — a session quietly running different pacing than the link named is
 * an unreproducible playtest, which is the same call `?home=` makes above.
 */
const knobs = directorKnobs(window.location.search);

if (knobs.refused.length > 0) {
  hud.setNotice(knobs.refused.join(" · "));
}

/*
 * The salience-decay knob (`witness/saga.ts`, Phase 6 decision 7 and O7).
 *
 * Here, before the witnessing layer is built, because it is a lens over the
 * *reading* rather than a change to the world: everything below this line asks
 * the simulation what a town holds, and the knob decides how much of what a town
 * heard years ago still counts. Nothing about the world, the log or the replay
 * moves — decay lives in the projections, which is what makes it replay-safe.
 *
 * Off unless the link says otherwise, and gated on the URL alone rather than on
 * the build flag: the session the owner judges it in is the deployed preview,
 * which is a production build. What was applied is said out loud, because a
 * session running different pacing than the link named is an unreproducible
 * playtest — the same call `?home=` makes above. Under `?fixture=1` what is said
 * is that the knob landed on a world with nothing in it that fades, which is the
 * same clause working: the fixture's lines are typed out rather than projected.
 */
if (witnessSource !== null) {
  const decay = applySalienceDecay(witnessSource, window.location.search, !authoredSource);

  if (decay !== null) {
    hud.setNotice(decay);
  }
}

/*
 * The debug window (`dev/debugpanel.ts`, wave 1.6 item 7).
 *
 * Built before the witnessing layer because the log is *handed to* that layer —
 * the seam is `witness/witness.ts`'s `WitnessWatch`, and a session with nobody
 * watching passes nothing and takes exactly the same decisions.
 *
 * **Built in every session since chunk D2**, on the owner's call: *"let's keep
 * the debug window always accessible with F3, I don't like having to open a
 * specific URL, and it's fine to have it while we develop."* It was already the
 * one development surface here gated on the link alone rather than also on
 * `import.meta.env.DEV` — the session that gets judged is the deployed preview
 * and that is a production build (`session/params.ts`) — and this is the end of
 * the same argument: a window the owner has to edit a URL to reach is not at
 * hand. `?debug=1` still means something and it is *open it before the first
 * frame*; the key is what opens it the rest of the time.
 *
 * **What an unwatched session now pays** is the honest answer to "it's fine
 * while we develop": one dynamic import, a hidden `<div>`, and once a second an
 * Info reading nobody is reading. The two lists are not in that — they are
 * asked for by the tab that draws them and by nothing else
 * (`dev/debugpanel.ts`'s `NearReading`). Nothing it does is hashed, recorded or
 * fed back: a session with the window open and one with it shut are the same
 * session, which is the licence this surface has always had.
 */
let debugLog: DebugLog | null = null;
let debugPanel: DebugPanel | null = null;
let debugWatch: LocalWatch | null = null;

/**
 * The mark that points at whatever the window is tracking (chunk D3).
 *
 * The owner's *"this list with places and bodies is very hard to read while they
 * change position … show the direction in a physical way so I can find it"*. It
 * lives beside the window because it is the window's control — a row is pressed
 * there and this is what the pressing shows — but it is drawn **every frame, and
 * whether or not the window is open**: tracking is the point and the window is
 * only where it starts (`dev/track.ts`).
 *
 * A developer aid on the window's own licence: it reads, it decides nothing, and
 * a session with a target tracked exports the same replay as one without. The
 * player-facing wayfinding is the legibility pass's and is a different question.
 */
let trackMarker: TrackMarker | null = null;

/**
 * The developer's own hands: flight, and the give tab (chunk Fx-3).
 *
 * **Behind nothing at all, since the owner's ruling of 2026-09-01** — *"it
 * should be on F3 without the dev thing"* — which is chunk D2's ruling about the
 * window itself applied to what is in it: a tool the owner has to edit a URL to
 * reach is a tool that is not at hand, and the session that gets judged is the
 * deployed preview, where `import.meta.env.DEV` is false anyway. Chunk Fx-3
 * shipped both behind `?dev=1` so a playtester's bare link grew neither; the
 * owner is the playtester, and *"keep it while we develop"* is the charter.
 * What the gate protected is still true without it: flight is host-side and
 * the world never learns of it, and a conjure is a recorded command, so a
 * session that used either exports a replay that verifies.
 *
 * They are two answers to one owner ask — *"a debug menu that gives me items …
 * I also need a debug option for flight/no clip"* — and they are kept apart
 * because they are different kinds of thing. **Flight decides nothing the
 * simulation can see**: the player's body is host-side by construction (Phase 3
 * decision 10), so suspending gravity is this client's business alone and the
 * world is never told. **The give door is a recorded command**: an item has to
 * exist sim-side to be drawn or held, so it crosses the wall and lands in the
 * replay — which is what makes a dev session's file an honest recording rather
 * than a lie (`sim_core::conjure`, `dev/give.ts`).
 */
let flight: Flight | null = null;

/** Seconds since the debug window started counting, which is the stamp on its lines. */
let debugSeconds = 0;

/** Seconds since it last re-read the world, and how many it waits for. */
let sinceDebugRefresh = 0;
let debugRefreshSeconds = 0;

// A block with no condition on it, which is what is left where `if (watching)`
// used to stand: the two constructors are still worth scoping, because `Log` and
// `Panel` are renamed imports that have no business in the rest of this file.
{
  const [
    { DebugLog: Log, DEBUG_REFRESH_SECONDS, localWatchOpen },
    { DebugPanel: Panel },
    { TrackMarker: Marker },
    { Flight: Wings },
    { GivePanel: Give, giveDoorOpen },
  ] = await Promise.all([
    import("./dev/debuglog"),
    import("./dev/debugpanel"),
    import("./dev/trackmarker"),
    import("./dev/flight"),
    import("./dev/give"),
  ]);

  debugRefreshSeconds = DEBUG_REFRESH_SECONDS;
  // Primed, so the *first* frame takes a reading rather than the frame a second
  // from now. The window is at hand in every session since chunk D2, which
  // means it can be opened on the first frame of one — and a window whose
  // answer to that is "nothing read yet" for a second is a window that looks
  // broken the one time somebody is watching it boot.
  sinceDebugRefresh = DEBUG_REFRESH_SECONDS;
  debugLog = new Log();

  // **The developer's hands, in every session** ({@link flight}, `dev/give.ts`
  // — the owner's ruling, on the window's own D2 precedent). The give panel is
  // still presence-checked against the boundary on `localWatchOpen`'s rule: a
  // client built against a wasm module from before chunk Fx-3 shows the four
  // tabs it always had rather than throwing on load.
  flight = new Wings(world.terrain);

  // Cast on the way in, exactly as the coverage reads are below: `GiveDoor` is
  // a structural interface of all-optional members, so a `WorldSource` shares no
  // *declared* property with it and TypeScript refuses the comparison. The
  // narrowing is the guard's job and the guard is the next line.
  const door = world as GiveDoor;
  const giving = giveDoorOpen(door)
      ? // The player's tile as a thunk, asked when a row is pressed: the window
        // refreshes once a second and a second is several strides, so a tile
        // taken on that cadence lands things where the player *was*
        // (`dev/give.ts`'s `feet`).
        new Give(door, () => player.tile, () => flight?.line ?? "")
      : null;

  debugPanel = new Panel(hudRoot, giving);
  // Built with the window and hidden until a row is pressed: one element, and a
  // session that never tracks anything never pays a frame for it
  // (`dev/trackmarker.ts`).
  trackMarker = new Marker(hudRoot);

  if (debugAtBoot(window.location.search)) {
    debugPanel.toggle();
  }

  // Presence-checked rather than assumed, on `birthDoorOpen`'s rule: a build
  // whose wasm boundary predates this chunk shows a window with the coverage
  // countdown missing rather than one that throws on the first refresh.
  debugWatch =
    witnessSource !== null && localWatchOpen(witnessSource as LocalWatch)
      ? (witnessSource as LocalWatch)
      : null;
}

const witnessing =
  witnessSource === null
    ? null
    : new Witnessing({
        source: witnessSource,
        terrain: world.terrain,
        scene: view.scene,
        hud,
        journal,
        people,
        director: knobs.config,
        // The town the claim seats its figure in, because at the instant it is
        // sent there is no promoted one to ask for (`witness/witness.ts`'s
        // `WitnessOptions.home`).
        home: spawnTown,
        // Where the player's three deeds go (`witness/witness.ts`'s
        // `PlayerDeeds`). The world satisfies the handle structurally, so
        // nothing is wrapped and nothing new crosses the seam: a claim, a
        // telling and a book are commands with every property a dig has —
        // queued, applied inside a tick, and written into the replay
        // (`docs/plans/player-as-figure.md` §4).
        deeds: world,
        // And where the hands reach (`witness/witness.ts`'s `PlayerHands`).
        // The same structural bargain the deeds are handed over under: the
        // world satisfies the handle, nothing is wrapped, and the assignment is
        // what the compiler checks the four spellings against.
        hands: world,
        // The simulation's walking cadence and the loop's own step, which is
        // what lets a body glide across the tile the local tier moved it over
        // instead of teleporting (`witness/scenes.ts`, wave 1.5). Both are
        // numbers this client is *given*: one by the wall, one by the loop.
        walkTicksPerStep,
        stepSeconds: TICK_SECONDS,
        // Where this layer says what it just did (`witness/witness.ts`'s
        // `WitnessWatch`). Spread rather than passed as `undefined`, because
        // `exactOptionalPropertyTypes` is on and *absent* and *present but
        // undefined* are different things here: a session that did not ask for
        // the window hands the option over not at all. `DebugLog` satisfies the
        // seam structurally — nothing from `dev/` is in the witnessing layer's
        // import graph.
        ...(debugLog === null ? {} : { watcher: debugLog }),
        // What the aiming prompt calls the interact verb. Spread like the
        // watcher above and for the same `exactOptionalPropertyTypes` reason,
        // with a sharper edge here: `null` is a *meaning* for this option — the
        // session with no key to name — so a session with a keyboard has to hand
        // it over not at all rather than as `undefined`.
        ...(touch ? { promptKey: null } : {}),
      });

/*
 * Who fills a page of the menu the strip switches to (chunk U-3,
 * `hud/panels/menu.ts`).
 *
 * The three keys need nothing here — `J` goes through the witnessing layer's
 * own toggle, which reads the world, fills the page and reveals it — but a tab
 * pressed with a finger or a mouse goes through nothing at all, and a page
 * switched to without being re-read is a page showing what the world looked
 * like the last time somebody opened it.
 *
 * So the strip asks for a fill, and what fills a page is the toggle its key
 * already fires: asked while its page is **not** showing, every one of the
 * three reads the world and shows the page. The *you* page has no toggle of its
 * own because it has no reader of its own — its sentence and its two counts
 * arrive inside the journal's view (`hud/panels/journal.ts`) — so it asks for
 * the journal's read and the screen selects itself over the top of it.
 */
hud.menu.fills((page) => {
  if ((page === "journal" || page === "you") && !hud.journal.open) {
    witnessing?.toggleJournal();
  }

  if (page === "map" && !hud.map.open) {
    witnessing?.toggleMap();
  }

  if (page === "inventory" && !hud.inventory.open) {
    witnessing?.toggleInventory();
  }
});

/**
 * What time the sky is drawn for (`render/daylight.ts`).
 *
 * The witnessing layer is the client's only clock — it is what watches the
 * date and measures what a day costs — so it is the session's time-of-day
 * source, and a world with no history has none: `?bootstrap=0` is a world where
 * the calendar exists and nobody is reading it, so the sky stays at fixed
 * morning and the camp verb never becomes available. Both are the honest
 * answers to "what time is it" in a world nothing has ever happened in.
 *
 * Under `?dev=1` the preview control wraps this and can override it, which is
 * why every read below goes through {@link timeOfDay} rather than touching
 * either one directly.
 */
const worldTime: TimeOfDaySource = witnessing ?? { phase: () => null };

/** The preview control, once it has loaded. Development only; see the import below. */
let daylight: DaylightPreview | null = null;

/** Whatever is currently deciding the time of day: the world, or the preview over it. */
function timeOfDay(): number | null {
  return (daylight ?? worldTime).phase();
}

// From here on there is a world, a player in it and a recorder running, so the
// save button has something to hand over.
sessionReady = true;

/**
 * Whether this session already knows who it is.
 *
 * **A restored file that carried a birth or a claim has a player figure in the
 * world** (requirement T-3): the `Born` or `Claim` was re-run with every other
 * command, so the simulation has the figure, the name and whatever the session
 * did with them. Asking such a session to read the book and choose a life would
 * be the start menus over a world that has already been lived in — and the
 * second birth would be a second identity for one person, which is the same
 * refusal the claim below is written under.
 *
 * A restored session whose log holds *no* birth — somebody declined the name and
 * saved — reads the book and is offered the lives over the world it loaded,
 * which is the honest answer for a session that never had an identity.
 */
const alreadyLived = restored !== null && (witnessSource?.playerFigure() ?? null) !== null;

/*
 * ------------------------- The book opens the game -------------------------
 *
 * Phase 6, wave 2C: the world is built, and the first thing a session shows is
 * the world's own history — the Book of Eras, read before anything else
 * (`hud/opening.ts`, decision O6). It resolves when the player closes it, and
 * everything below this line is the session exactly as it was.
 *
 * Here rather than anywhere else for three reasons, and each one is about what
 * has and has not happened yet:
 *
 * - **After the world and after the witnessing**, because the book is that
 *   layer's own read — pulled on the day boundary with the rest of the bulk and
 *   handed over here rather than derived twice ({@link Witnessing.eras}). A
 *   world with no history has none: `?bootstrap=0` reads "unwritten" and the
 *   session simply starts.
 * - **Before the controls exist**, so the four gestures that close it cannot
 *   collide with anything: Enter is not yet a key that walks and Escape is not
 *   yet the key that drops the pointer lock (`input/controls.ts` binds on
 *   `document`, from the constructor below).
 * - **Before the click-to-play prompt**, because the prompt is the invitation to
 *   start and the book is what a session opens *with*. A player who clicks
 *   straight through the prompt would otherwise never see it, which is the trap
 *   every first-run moment in this file is written around.
 *
 * Night-start against book-start is the owner's taste call and `?book=0` is how
 * it is judged: the same session, the same world, no book (`witness/saga.ts`).
 *
 * A session restored from a file that already holds a birth reads *declined*
 * here without the book being drawn ({@link alreadyLived}). It is the read a
 * session that was never asked has always carried, and it is the one this
 * client's vocabulary has — `hud/opening.ts` owns those three words and adding a
 * fourth for a door outside its file would be this file voting on its type.
 */
const opened = alreadyLived
  ? "declined"
  : await openTheBook(hud, witnessing?.eras ?? null, window.location.search);

/*
 * ------------------------- And where do you enter it? -------------------------
 *
 * Phase 6, wave 3C: the book closes on a question, and the world answers it with
 * lives it has already lived (`hud/origins.ts`, decision 3). The player picks
 * one, types a name, and `Command::Born` binds it — or declines at any step, is
 * asked for a name anyway, and enters the world with one nobody has written down
 * yet (decision 4, *two doors*, executed in full at the owner's ruling of
 * 2026-08-24: **the typed name lives in the start menus on every path**).
 *
 * Here, in the same seam the book is read in, for the same three reasons: after
 * the world and the witnessing because the lives are that source's own read;
 * before the controls exist so the keys that choose a life cannot collide with
 * the keys that walk; and before the click-to-play prompt because a player who
 * clicked straight through would otherwise be born without being asked.
 *
 * The offer is read *here* rather than inside the screen so that a session which
 * skips the screen still has it for the debug handle: what this build was able to
 * offer, and what it refused on the way in, is a fact about the boundary rather
 * than about what somebody chose (`witness/origins.ts`).
 */
const offer: OriginsOffer = birthLives === null ? EMPTY_ORIGINS : readOrigins(birthLives);

// The last argument is whether there is anywhere for a name to land: a world with
// no history has no towns and no records, so nothing there could ever cite a
// player and it is not asked for one (`hud/origins.ts`). Everything else about the
// flow is that file's.
//
// A session that already lived is not asked at all: the name is in the world it
// loaded, and the screen is skipped rather than answered ({@link alreadyLived}).
const born: BornSession = alreadyLived
  ? { read: "declined", chosen: null, name: "" }
  : await chooseAnOrigin(
      hud,
      offer,
      birthDoor,
      window.location.search,
      witnessing !== null,
      // The lives' lead is the book's own question here; the waking is read out
      // of the same source the cards came from (`hud/origins.ts`'s
      // `WakingReading`) — `birthLives`, not `witnessSource`, so a `?bornfake=1`
      // session stays silent without a branch (chunk U-4,
      // `docs/decisions/ui.md` §5.8).
      null,
      birthLives,
    );

/**
 * What this session is called, whichever card took it. Empty means unnamed.
 *
 * The one piece of the start menus that outlives them, and it lives **here, for
 * the tab**, exactly like the journal and the walked ground: nothing is written
 * to storage, so a reopened link is a fresh session that is asked again (Phase 4
 * decision 7's honesty rule about what a playtest hands back — a name quietly
 * remembered from a previous sitting would be a session nobody could reproduce).
 *
 * A born session already put it in the log at tick zero; every other session puts
 * it there itself, a few lines below, before the first frame is drawn.
 */
const called = born.name;

if (born.chosen !== null) {
  // The command is already queued by the screen that chose it; what the
  // witnessing layer is handed is the *card*, so the trace view can show the
  // records the life stands on (`witness/witness.ts`'s `bornAs`). Who the player
  // is comes back out of the world on the next step like any other claim —
  // **a born player is a claimed player**, which is why nothing below this line
  // needed a second word for it.
  witnessing?.bornAs({
    chosen: born.chosen,
    name: born.name,
    queued: born.read === "born",
  });
}

/*
 * ------------------------- And the record takes the name -------------------------
 *
 * The other door, closed here rather than a minute into play. **The owner's
 * ruling of 2026-08-24, sharpened**: *"identity settles fully before spawning"* —
 * a session that gave a name at the menu and took no life claims it at spawn, the
 * in-world confirmation card is gone, and no identity beat of any kind survives
 * past this line (`docs/plans/phase-6-plan.md`, the playtest amendments).
 *
 * Here, and not later, because this is the earliest instant at which every fact
 * the claim needs is settled and nothing has been drawn yet: the start menus have
 * resolved so the name is known, the witnessing layer was built above and its
 * constructor already read which town the player is standing in, and the loop has
 * not started — so the command is in the queue before the world's first tick
 * consumes anything. It is the birth's own seam, one clause over: `Command::Born` above,
 * `Command::Claim` here, both decided before the first frame.
 *
 * Two sessions pass through untouched, and both are rules rather than cases:
 *
 * - **A session that was born.** The life it took is already in the log and a born
 *   player is a claimed player; claiming again would be a second identity for one
 *   person. The clause reads off what was *queued* rather than off `claimed`,
 *   because at this point in the boot nothing has ticked, so the world has not
 *   answered yet either way. The other side of that: a birth the simulation
 *   silently refuses leaves the session unnamed for good — the tick has no error
 *   channel and there is no beat left to fall back on, which is what the ruling
 *   chose.
 * - **A session with no name.** Skipping the menu's naming step is a real answer
 *   and an unnamed player is simply never cited (Phase 5 decision 1). The guard is
 *   the method's, spelled once (`witness/witness.ts`'s `claimTheName`), and a
 *   world with no history reaches it with nothing anyway: `?bootstrap=0` has no
 *   witnessing layer, is asked for no name, and is interrupted by nothing.
 *
 * Sent once, and never chased: it is a statement in the boot sequence rather than
 * a step in the loop, so there is nothing here that could retry a claim the
 * simulation quietly refused.
 */
if (!alreadyLived && born.read !== "born") {
  witnessing?.claimTheName(called);
}

// What was built, and what it cost, on the prompt the player dismisses to start:
// the one place a session says out loud that the world it is standing in has a
// past, and the only place the wait it just sat through is accounted for. It
// goes when the pointer is locked, which is the right lifetime for it.
//
// A loaded session says *loaded* rather than *built*, and the seconds are the
// whole of what the file cost — the world rebuilt and every recorded tick re-run
// (`session/restore.ts`).
const raising = restored === null ? "built" : "loaded";
const worldLine =
  witnessSource === null
    ? `\n\nno history in this world · ${raising} in ${buildSeconds.toFixed(1)} s`
    : `\n\nyear ${witnessSource.date().year} · ${raising} in ${buildSeconds.toFixed(1)} s`;

// Assembled in three pieces rather than one expression, because since the touch
// pass each piece has two spellings and a nested pair of conditionals over the
// whole sentence would be a prompt nobody could read the source of. The *shape*
// is unchanged: how to start, then the verbs where there are any, then how to
// stop and hand the session back, then what was built.
const opening = touch ? TOUCH_PROMPT : PLAY_PROMPT;
const verbs = witnessing === null ? "" : touch ? TOUCH_WITNESS_PROMPT : WITNESS_PROMPT;
const leaving = touch ? TOUCH_SESSION_PROMPT : SESSION_PROMPT;
const playPrompt = opening + verbs + leaving + worldLine;

const controls = new Controls(
  canvas,
  (locked) => {
    // The overlay answers "why is the mouse free" with "click to come back" —
    // wrong answer while a reading panel is the reason it is free (the journal
    // hands the lock back so the wheel can scroll it; see the toggle handling
    // in `beginFrame`). The panel's own close puts the prompt back if the lock
    // does not come back with it.
    //
    // **One question, asked in one place** (`hud/hud.ts`'s `reading`, Phase 5
    // decision 10). This file used to spell the same disjunction at three
    // sites, so the map joining them would have meant finding all three.
    hud.setOverlay(locked || hud.reading ? null : playPrompt);
  },
  (message) => {
    hud.setNotice(`pointer lock failed — ${message} · arrow keys still look`);
  },
  touch,
);

/**
 * The two thumbs, where there are two thumbs (`input/touch.ts`).
 *
 * Built here, immediately after `Controls`, because it is the same layer read a
 * second way and it has nothing to say to anything else: what it produces goes
 * into the object above, so the frame loop below drains the doors it always
 * drained and contains not one branch about touch.
 *
 * `null` on a desktop, and that is the whole cost of the pass to a mouse
 * session: no DOM, no listeners, one boolean.
 *
 * Late in the boot sequence on purpose. The HUD orders by document position
 * alone (`hud/hud.ts`), and everything the start menus draw is appended before
 * this line — so the buttons cannot float over the Book of Eras or the birth
 * screen, which have both been closed by the time this runs.
 */
const touchControls =
  touch === false
    ? null
    : new TouchControls({ root: hudRoot, surface: canvas, controls, dev });

hud.setOverlay(playPrompt);

if (restored !== null) {
  // **The one thing a loaded session has to be told** (requirement T-3): a
  // replay holds the world and the last tile the client named, and holds nothing
  // about the player — the journal, the walked map and the people met are
  // host-side (Phase 3 decision 10), so they are gone and the notice says so
  // rather than letting an empty journal say it (`session/restore.ts`).
  hud.setNotice(restoredLine(restored.restored.ticks));

  // And where the load landed, for a driver: the world's tick and its hash, at
  // the instant before the loop starts moving both. Written where the
  // self-check verdict is written, and for the same reason — a script that
  // reads it has no handle yet (`scripts/live-smoke.mjs`).
  const landed = world.stats();

  document.body.dataset["restored"] = `${landed.tick} ${landed.worldHash}`;
}

// The dev skip's button. Gated on the URL param alone rather than on `dev`
// (the build flag above), deliberately: the owner dry-runs the *deployed*
// preview, which is a production build, and unlike the trace view nothing this
// button surfaces leaks a register the fiction protects — it only declines to
// sleep through mornings the director already scheduled. A playtester's bare
// URL never carries the param, so the session the protocol tests never grows
// the verb.
if (witnessing !== null && devRequested(window.location.search)) {
  const skipWitnessing = witnessing;

  new SaveButton(
    hudRoot,
    "skip ahead",
    "dev: runs the world a day at a time and stops the morning something arrives" +
      " — at most a month per press",
    () => {
      if (skipDaysLeft > 0) {
        return;
      }

      if (skipWitnessing.reacting !== null) {
        hud.setNotice("skip: the town is mid-reaction — let it finish first");

        return;
      }

      skipDaysLeft = SKIP_DAYS_MAX;
    },
    "skip",
  );
}

/** The tile under the crosshair as of the last frame drawn, or `null` if nothing is in range. */
let target: RayHit | null = null;

/**
 * Whether an open reading panel is holding the pointer lock's place.
 *
 * Set when the journal (or the trace view) opens out of a locked session and
 * releases the lock to be scrollable; consumed when it closes and the lock is
 * asked for back. `false` the whole time for a panel opened from the unlocked
 * prompt — nothing was borrowed, so nothing is returned.
 */
let readingTookLock = false;

/**
 * How many days one press of the dev skip may cross before giving up.
 *
 * A little over the entity-turn cadence, so a press from any quiet day always
 * reaches the next monthly turn — the morning everything in the abstract world
 * fires — and stops there or sooner. **Unchanged by the calendar retune**,
 * because what it counts is *days* and the entity turn is still 28 of them.
 *
 * What did change is what a press costs, and it is worth writing down rather
 * than discovering: a day is thirty times the ticks it was (plan decision 7)
 * and a tick costs about a fifth of a millisecond, so a full month of skipping
 * is on the order of three wall minutes of budgeted frames rather than a blink.
 * The press stops the moment anything lands, which in a live world is usually
 * long before the month is out, and it says "days pass" with the date on it the
 * whole way — so the cost shows rather than hangs. Shortening it would trade
 * away the one thing it is for: reaching the next entity turn from any day.
 */
const SKIP_DAYS_MAX = 30;

/**
 * How much of one frame the fast-forward verbs may spend stepping, in
 * milliseconds. PROVISIONAL.
 *
 * Shared by the dev skip and the camp verb, which is the point: plan decision 8
 * says sleep runs on the skip's machinery, and a second budget would be a
 * second thing to keep in step.
 *
 * **Re-derived at twenty-minute days, and it had to move.** Eight milliseconds
 * left most of a 60 Hz frame for drawing, which was the right trade back when a
 * whole day was a blink to cross. A tick of this client costs about a fifth of
 * a millisecond measured in a headless session — the simulation, the interest
 * command and the witnessing layer together — so a day at the retuned calendar
 * is around seven seconds of *unavoidable* work however it is spread, and at
 * eight milliseconds a frame it took half a minute of wall clock to sleep
 * through one.
 *
 * Twenty-four is three frames' worth of that budget and it is spent in exactly
 * the two situations where a smooth sixty is worth nothing: the camp verb draws
 * a black screen and the skip draws a world going past too fast to read. The
 * frame rate falls to about thirty while either runs, which is a thing nobody
 * is looking at, and the tab keeps answering input and animating the fade.
 *
 * The seven seconds themselves are not negotiable and should not be optimised
 * away: **every tick genuinely runs** is the whole claim (plan decision 8), and
 * a night that cost nothing would be a night that did not happen.
 */
const SKIP_FRAME_BUDGET_MS = 24;

/** Days the dev skip still owes. Zero when it is not running, which is almost always. */
let skipDaysLeft = 0;

/**
 * How dark the body's own crossing has the screen (`host/rest.ts`).
 *
 * Its own fade, so the camp's is untouched: the two can be true at once — a
 * player knocked out during a camp is a real sequence — and what is drawn is the
 * darker of them ({@link stepCamp}).
 */
let darkFade = 0;

/**
 * One fixed step of the whole client, without a frame around it
 * (`host/fastforward.ts`).
 *
 * The same three calls the host loop's `step` makes, in the same order, so
 * every day boundary the client would have noticed is noticed in place. This is
 * the whole of what "fast-forward" means here: **the ticks are real**, the
 * commands land on their true ticks, and a replay of a session that slept
 * through a night verifies exactly like one that stayed awake (plan decision 8).
 */
const fastForwardHost: FastForwardHost = {
  tick: () => {
    world.setInterest(player.tile, INTEREST_RADIUS_CHUNKS);
    world.step();
    witnessing?.step(TICK_SECONDS);
  },
  date: () => witnessSource?.date() ?? null,
  now: () => performance.now(),
  stand: () => {
    standAsTheDarkFalls();
  },
};

/**
 * Tells the world where the player is lying down, as a crossing opens.
 *
 * **One function for all four crossings** (chunk Fx-7): the camp, the wait and
 * the dev skip state it through {@link FastForwardHost.stand} and the body's two
 * state it through `host/rest.ts`'s `RestWorld.stand`, and it is the same
 * sentence about the same tile — so it is written once here rather than twice in
 * two object literals that would then be free to drift.
 *
 * The trigger underneath is unchanged and does the deciding
 * (`witness/witness.ts`'s `standIfWatched`): a night nobody in the town wants
 * anything from the player still costs the command log nothing, and a night
 * spent beside a guard who wants them is a night the guard can reach them in.
 */
function standAsTheDarkFalls(): void {
  witnessing?.standIfWatched(player.tile);
}

/**
 * The crossing the two interactive verbs share.
 *
 * One instance for both, because they are mutually exclusive by construction —
 * the skip refuses to start while a camp is running and the camp refuses while
 * the skip is — and one crossing is easier to reason about than two that must
 * never both be open. The debug handle gets its own below: a headless driver is
 * not holding the keyboard and must not be able to interleave with either.
 */
const fastForward = new FastForward(fastForwardHost);

/**
 * What the crossings in the dark ask of the world (`host/rest.ts`, chunk Fx-5).
 *
 * Three lines of wiring and not one decision: the reading is the witnessing
 * layer's own ({@link Witnessing.bodyReading} — five of the simulation's
 * verdicts about the player's body, composed nowhere), the sentence about a
 * taking is the town board's, and the stand is the trigger that already exists
 * at the cadence it already has.
 *
 * The taking's sentence goes through the **epitaph** door on purpose: a death
 * and an arrest are two records on one board, read in the town the player is
 * standing in, and one door means the two can never come to be phrased by
 * different rules (`witness/witness.ts`).
 */
const restWorld: RestWorld = {
  read: () => witnessing?.bodyReading ?? NOBODY_RESTING,
  said: (record) => witnessing?.epitaph(record) ?? null,
  stand: () => {
    standAsTheDarkFalls();
  },
};

/**
 * The blackout and the rest verb, which are one machine (`host/rest.ts`).
 *
 * Its own crossing rather than {@link fastForward}'s, and that is the one place
 * it differs from the camp: a camp crosses until the **date** turns and this
 * crosses until the **simulation says so**, so it runs fixed slices and asks
 * between them. Both burn real ticks, which is the whole claim (plan decision
 * 8).
 *
 * It is deliberately *not* mutually exclusive with the camp's crossing: a player
 * knocked out during a night is a real sequence and neither half of it is a
 * mistake. The two verbs a player presses are refused against it
 * ({@link canCamp}, {@link startCrossing}), because those are choices.
 */
const rest = new Rest(fastForwardHost, restWorld);

/**
 * The dev skip: crosses quiet days at speed and stops the morning something
 * lands.
 *
 * One day per iteration, each one a crossing (`host/fastforward.ts`) — the
 * client owns no calendar arithmetic, so it steps until the *date* turns and
 * never counts to a number. The stop rule is the director's own schedule read
 * back rather than interpreted ({@link Witnessing.beatsAhead}): a beat whose
 * `earliest` is the morning just reached means the reaction begins on the next
 * ordinary frame, which is the moment to hand the world back at full speed —
 * the whole scene then plays from its first breath. Which mornings matter is
 * the simulation's list; the skip only declines to sleep through them.
 *
 * **The budget is now inside the day as well as around it**, and that is the
 * retune's doing: one budget check per day was fine when a day was a blink, and
 * at thirty times the ticks it would be a third of a second of frozen tab per
 * day crossed. The crossing carries its own deadline and returns `"budget"`
 * when the frame is spent, keeping its place for the next one.
 */
function runSkip(): void {
  const deadline = performance.now() + SKIP_FRAME_BUDGET_MS;

  while (skipDaysLeft > 0) {
    if (fastForward.crossing === null && !fastForward.start()) {
      skipDaysLeft = 0;

      return;
    }

    const outcome = fastForward.advance(deadline);

    if (outcome === "budget") {
      const today = witnessSource?.date() ?? null;

      hud.setNotice(`days pass — ${today === null ? "" : formatWorldDate(today)}`);

      return;
    }

    if (outcome !== "morning") {
      skipDaysLeft = 0;
      hud.setNotice(`skip: the day would not turn (${outcome})`);

      return;
    }

    skipDaysLeft -= 1;

    const today = witnessSource?.date() ?? null;

    if (today === null) {
      skipDaysLeft = 0;

      return;
    }

    const landing = (witnessing?.beatsAhead() ?? []).some(
      (beat) =>
        beat.earliest.year === today.year && beat.earliest.dayOfYear === today.dayOfYear,
    );

    if (landing) {
      skipDaysLeft = 0;
      hud.setNotice(`skip: ${formatWorldDate(today)} — something arrives this morning`);

      return;
    }

    if (skipDaysLeft === 0) {
      hud.setNotice(`skip: nothing arrived by ${formatWorldDate(today)} — a quiet month`);

      return;
    }
  }
}

/*
 * ------------------------------ Making camp ------------------------------
 *
 * Plan decision 8's other half: **sleep is fast-forward, not time travel.**
 * Pressing C after dark runs the world to the next morning on the crossing
 * above — every tick, in order, on the same budget the skip uses — and the
 * screen goes dark while it does. Nothing is skipped except the *watching*.
 *
 * "Make camp" rather than "sleep" or "rest", and the words are the whole of the
 * fiction here: there is no bedroll, no shelter and no interior in this build,
 * so a player standing in a field who presses a key has made camp and has not
 * gone to bed. It is also plain — the standing rule is that a word a player has
 * to look up is a prompt that failed (`witness/witness.ts`'s labels).
 *
 * Nothing about it pre-echoes the world: the only thing said afterwards is the
 * date, which is already on the readout, and the only thing that decides when
 * it ends is the calendar turning.
 */

/*
 * The two prompts, and what the dark says while either of them runs, live in
 * `hud/crossing.ts` since chunk Fx-2 — the label's contract and the crossing's
 * own voice are one subject and one file, checkable without a session behind
 * them (`dev/selfcheck/telling.ts`).
 */
const CAMP_PROMPT = campPrompt(touch);

const WAIT_PROMPT = waitPrompt(touch);

/**
 * And the third, which is offered by the body rather than by the sky (chunk
 * Fx-5).
 */
const REST_PROMPT = restPrompt(touch);

/**
 * How long the screen takes to go dark, and to come back, in seconds of frame
 * time.
 *
 * Long enough to read as a decision rather than a cut, short enough that it is
 * not the slowest part of sleeping through a night — the crossing itself is a
 * second or two of budgeted frames. Frame time rather than stepped time,
 * because the world underneath is running at whatever speed the budget allows
 * and a fade measured in its seconds would be over before the first frame drew
 * (`hud/hud.ts`'s `setFade`).
 */
const CAMP_FADE_SECONDS = 0.6;

/** Where the camp verb is in its four states. */
type CampState = "awake" | "closing" | "sleeping" | "waking";

let campState: CampState = "awake";

/**
 * Whether the crossing that is running is the wait rather than the camp.
 *
 * **One crossing, two verbs, and they are mutually exclusive by construction**
 * ({@link fastForward}): the state machine below is the camp's and the wait
 * borrows it whole, so this is the one bit that says which one the player asked
 * for. What it changes is two sentences and nothing else — a waking line about
 * first light is a lie about an afternoon.
 *
 * The alternative was a second state machine, and it would have been a second
 * thing that must never open the same crossing.
 */
let waiting = false;

/** How dark the screen is, 0 to 1. Advanced on frame time; see {@link CAMP_FADE_SECONDS}. */
let campFade = 0;

/**
 * Whether the player may make camp right now.
 *
 * Two conditions and both are facts rather than policy: it has to be dark
 * (`render/daylight.ts`'s `isNight`, over whatever the sky is currently drawn
 * from), and the client has to know what time it is at all — a session that has
 * not watched a day go by is a session whose sky is a fallback, and a verb
 * offered against a fallback would be a verb that skipped a day the player
 * never saw.
 */
function canCamp(): boolean {
  const phase = timeOfDay();

  return (
    campState === "awake" &&
    skipDaysLeft === 0 &&
    // And the body is not crossing time of its own accord (chunk Fx-5): a
    // player who is out cold is not choosing anything, and one who is resting
    // has chosen already.
    !rest.crossing &&
    phase !== null &&
    isNight(phase)
  );
}

/**
 * Whether the player may wait right now.
 *
 * {@link canCamp} with the night taken out, which is the only difference between
 * the two verbs. The other two conditions are the same and they are the same
 * *facts*: nothing else may be crossing, and the client has to know what time it
 * is at all — a session whose sky is a fallback is a session that has not
 * watched a day go by, and a verb offered against a fallback would skip a day
 * the player never saw.
 */
function canWait(): boolean {
  return campState === "awake" && skipDaysLeft === 0 && !rest.crossing && timeOfDay() !== null;
}

/**
 * Whether the player may lie up right now.
 *
 * The odd one of the three, and the difference is where the offer comes from:
 * camp and the wait are offered by the **sky**, and this is offered by the
 * **body** ({@link Witnessing.bodyReading}'s `unfit`, which is three bits the
 * simulation set). Nothing else about it differs — one crossing at a time, on
 * the same terms.
 */
function canRest(): boolean {
  return (
    campState === "awake" && skipDaysLeft === 0 && !rest.crossing && restWorld.read().unfit
  );
}

/** Starts a night. Says why not, when there is no night to start. */
function makeCamp(): void {
  startCrossing(false);
}

/** Starts a wait. Says why not, when there is no clock to wait on. */
function waitAWhile(): void {
  startCrossing(true);
}

/**
 * Opens the crossing for whichever of the two verbs asked for it.
 *
 * One function because they are one machine (the {@link waiting} note): the fade
 * closes, the crossing opens, the world runs and the fade lifts, and the two
 * verbs differ in when they may be pressed and what is said afterwards. Written
 * as one rather than two so that the refusals cannot drift apart — *"something
 * else is already running"* has to mean the same thing to both, or they stop
 * being mutually exclusive.
 */
function startCrossing(wait: boolean): void {
  if (campState !== "awake") {
    return;
  }

  if (skipDaysLeft > 0) {
    hud.setNotice("the skip is already running — let it finish first");

    return;
  }

  // And the body's own crossing, refused in the words the rest verb refuses
  // these two with (chunk Fx-5): one sentence for all four, because they are one
  // crossing at a time and a refusal that read differently in each direction
  // would be four verbs that had stopped agreeing about that.
  if (rest.crossing) {
    hud.setNotice(REFUSED_ALREADY_CROSSING);

    return;
  }

  if (!(wait ? canWait() : canCamp())) {
    hud.setNotice(
      wait
        ? "there is no clock to wait on in this world"
        : "there is nothing to wait out yet — camp once it is dark",
    );

    return;
  }

  waiting = wait;
  campState = "closing";
}

/**
 * Runs the night while the screen is dark.
 *
 * Called from the top of a frame, like the skip, so everything after it sees
 * the world already standing at the morning it reached. The crossing was opened
 * when the fade finished ({@link stepCamp}), which is what makes the guard's
 * tick count a count of *this* night.
 */
function runSleep(): void {
  const outcome = fastForward.advance(performance.now() + SKIP_FRAME_BUDGET_MS);

  if (outcome === "budget") {
    return;
  }

  campState = "waking";

  if (outcome === "morning") {
    const today = witnessSource?.date() ?? null;
    const when = today === null ? "" : formatWorldDate(today);

    // Two sentences for two verbs, and the difference is what makes the second
    // one honest: a crossing always ends at a day boundary, so a *wait* ends at
    // first light too — and telling somebody who waited out an afternoon that
    // they "woke" would be the client narrating a nap nobody took.
    hud.setNotice(
      waiting ? `time passes — ${when}` : `you wake at first light — ${when}`,
    );

    return;
  }

  // The night did not pass, which in a world with a calendar means the crossing
  // hit its spin guard (`host/fastforward.ts`). Said out loud rather than
  // retried: a day this client cannot cross is a thing somebody needs to know
  // about, not a thing to sit through twice.
  hud.setNotice(
    waiting
      ? "the hours would not pass — this world keeps no calendar"
      : "you wake where you lay — this world's night did not pass",
  );
}

/**
 * Whether the session is standing at its own death screen.
 *
 * While it is, the world does not step and nothing the player presses reaches
 * it: the origins panel binds its own keys on `document` (`hud/origins.ts`) and
 * so does {@link Controls}, so a menu opened over a running session has to take
 * the world's hands off the keyboard rather than share it
 * ({@link Controls.forget}).
 *
 * **The clock stops with it**, which is the one thing about permadeath this
 * client decides: the world already remembers everything it needs to, and a
 * town going about its business behind a menu nobody can act through would be
 * hours passing that the player did not spend.
 */
let mourning = false;

/**
 * What the screen says when the log has nothing to say about the death.
 *
 * A record this world does not have, or a session with no town to post the
 * notice in — both are states that should not happen and neither is worth an
 * invented sentence about how somebody died. What is said instead is the one
 * thing that is certainly true (`witness/witness.ts`'s composing rule: where the
 * client cannot know, it says the honest generic).
 */
const DEATH_UNRECORDED = "and that was the end of you.";

/**
 * Takes the session back to the origins screen over the world that killed it.
 *
 * **Permadeath, and it is exactly this** (Phase 7 plan, decision 7): the
 * player's figure died like any other figure, the log wrote it down like any
 * other death, and what a client owes the moment is the log's own sentence and a
 * door back in. **Nothing about the late figure is undone** — their record,
 * their line, their deeds and whatever the rumour layer is still carrying about
 * them are where they were, which is the whole of what *the world remembers*
 * means here.
 *
 * The screen is the *same* screen a session opens on, with one thing different:
 * the sentence it stands on is the death rather than the question
 * (`hud/origins.ts`'s `lead`). The lives it then offers are re-read from the
 * world as it is *now*, so the heirs of the line that just ended are on it —
 * and if the line has run out, the other three doors still are, which is why
 * this is a screen rather than an ending.
 *
 * Awaited from the frame loop and never chased: while it is open the world is
 * still, and when it resolves the session goes on in the same tab, in the same
 * world, as somebody else.
 */
async function mourn(record: EventId): Promise<void> {
  if (mourning) {
    return;
  }

  mourning = true;
  controls.forget();
  controls.releaseLock();
  hud.setOverlay(null);

  // The record's own words, through the simulation's written register — the
  // killer, the weapon and the cause are on the record and the sentence is the
  // world's (`witness/witness.ts`'s `epitaph`). Nothing here composes one.
  const said = witnessing?.epitaph(record) ?? null;
  const again = await chooseAnOrigin(
    hud,
    birthLives === null ? EMPTY_ORIGINS : readOrigins(birthLives),
    birthDoor,
    window.location.search,
    witnessing !== null,
    said ?? DEATH_UNRECORDED,
    birthLives,
  );

  // The bookkeeping about *whose* session this is, and nothing else: the world
  // is untouched, and who the player is now comes back out of it on the next
  // step like any other claim ({@link Witnessing.liveAgain}).
  witnessing?.liveAgain();

  if (again.chosen !== null) {
    witnessing?.bornAs({
      chosen: again.chosen,
      name: again.name,
      queued: again.read === "born",
    });
  } else if (again.read !== "born") {
    // The other door, on the boot sequence's own terms: a session that took no
    // life still names itself, and the claim is what puts that name in the log.
    witnessing?.claimTheName(again.name);
  }

  mourning = false;
  hud.setOverlay(playPrompt);
  controls.requestLock();
}

/**
 * Answers the rest key (`host/rest.ts`, chunk Fx-5).
 *
 * The camp's and the wait's third sibling, and the only one of the three whose
 * availability is a fact about the **body** rather than about the sky — so the
 * machine owns the *fit* half of the question and this owns the same half
 * {@link startCrossing} owns for the other two: **one crossing at a time.**
 *
 * The exclusion is only asked while the dark is not already up. A press during a
 * blackout is the option the ruling added and it is the machine's — a body that
 * has gone under during a camp is still allowed to say it would rather stay
 * down until it is fit.
 */
function lieDown(): void {
  if (!rest.crossing && (campState !== "awake" || skipDaysLeft > 0)) {
    hud.setNotice(REFUSED_ALREADY_CROSSING);

    return;
  }

  const said = rest.press();

  if (said !== null) {
    hud.setNotice(said);
  }
}

/**
 * Advances the fade and moves the camp between its states.
 *
 * Called once a rendered frame with frame time. The crossing is opened at the
 * moment the screen finishes going dark rather than at the key press, so
 * nothing runs while the player can still see — a night that started under a
 * half-transparent screen would be a night the player watched flicker past.
 */
function stepCamp(frameSeconds: number): void {
  if (campState === "closing") {
    campFade = Math.min(1, campFade + frameSeconds / CAMP_FADE_SECONDS);

    if (campFade >= 1) {
      // **And the crossing states where the player lay down as it opens**
      // (chunk Fx-7, closing Fx-2's filing): `start` says it, here rather than
      // at the key press, because the fade is six tenths of a second of walking
      // and the tile that matters is the one they are standing on when the world
      // starts running without them ({@link FastForwardHost.stand}).
      campState = fastForward.start() ? "sleeping" : "waking";

      if (campState === "waking") {
        hud.setNotice("this world keeps no calendar to sleep through");
      }
    }
  } else if (campState === "waking") {
    campFade = Math.max(0, campFade - frameSeconds / CAMP_FADE_SECONDS);

    if (campFade <= 0) {
      campState = "awake";
    }
  }

  // **The body's fade is the maximum of the two, not a second element.**
  // The two can be true at once — a player knocked out during a camp is a real
  // sequence — and a screen that took the darker of them is a screen that never
  // brightens in the middle of one.
  if (rest.crossing) {
    darkFade = Math.min(1, darkFade + frameSeconds / CAMP_FADE_SECONDS);
  } else {
    darkFade = Math.max(0, darkFade - frameSeconds / CAMP_FADE_SECONDS);
  }

  hud.setFade(Math.max(campFade, darkFade));

  // **Every crossing speaks now, including the two nobody pressed a key for**
  // (chunk Fx-5). The clause that said *only the two verbs a player pressed on
  // purpose* is what left the owner looking at five minutes of black with
  // nothing on it: the wait finding's own diagnosis — a screen that never
  // changes reads as a hang — is true whoever asked for the dark.
  //
  // The body's crossing wins the line where both are running, because *you are
  // out cold* is the news and *the night passes* is the weather.
  const crossing =
    rest.kind ?? (campState === "awake" ? null : waiting ? "hours" : "night");

  hud.setCrossing(
    crossing === null
      ? null
      : crossingLine(crossing, timeOfDay(), witnessSource?.date() ?? null),
    rest.option(touch),
  );
}

const loop = new HostLoop({
  beginFrame(frameSeconds: number): void {
    // The two fast-forwards burn their ticks first, so everything after them —
    // input, the HUD, the witnessing — sees the world already stood at the
    // morning it reached. Never both in one frame: each refuses to start while
    // the other is running.
    if (skipDaysLeft > 0) {
      runSkip();
    }

    if (campState === "sleeping") {
      runSleep();
    }

    // And the third, which is the one nobody asked for and the one somebody
    // pressed a key for: the body's own crossing (`host/rest.ts`). It reads the
    // simulation's verdict itself and takes the frame away for as long as that
    // verdict lasts — before the input rather than after, so a blow that landed
    // on the last step takes this frame from a player who cannot act anyway.
    //
    // What comes back is the sentence the dark lifts on, including what the
    // town did while nobody could see it (chunk Fx-5).
    const woke = rest.advance(performance.now() + SKIP_FRAME_BUDGET_MS, mourning);

    if (woke !== null) {
      hud.setNotice(woke);
    }

    // **The end of a life takes the frame** ({@link mourn}). Read before the
    // input below, so the first frame after a killing blow is a frame nothing
    // the player is leaning on reaches the world.
    const ended = witnessing?.playerDeath ?? null;

    if (ended !== null && !mourning) {
      void mourn(ended);
    }

    if (mourning) {
      // Drained and dropped: the panel has its own keys and the world has none
      // while it is up, so a key pressed at a menu must not be a swing waiting
      // for the next life ({@link Controls.forget}).
      controls.forget();
      // **And the screen's own key with them** (chunk U-3), which `forget`
      // deliberately does not clear: the panel counts survive a release because
      // *"a journal or a map is a request about the session"*
      // (`Controls.releaseAll`), and that is right for `J` at a menu and wrong
      // for `Tab` — because `Tab` is the key the death screen steps its own
      // cards with (`hud/origins.ts`'s `moveFor`). A player choosing an heir
      // would otherwise be choosing to open the menu on the next life's first
      // frame, once per card they walked past.
      controls.takeMenuToggles();

      return;
    }

    const look = controls.takeLook(frameSeconds);
    player.look(look.yaw, look.pitch);

    // The key card goes the first time the player does anything at all. Checked
    // here rather than pushed from the input layer because it is a fact about
    // the session and not an event — `dismiss` is idempotent and this is one
    // boolean per frame (`hud/panels.ts`).
    //
    // The same fact tells the director the room is occupied: the loop steps the
    // world while the click-to-play overlay is still up, so a delivery released
    // before this would play to nobody (`witness/director.ts`). Idempotent for
    // the same reason and at the same cost.
    if (controls.hasPlayed) {
      hud.keys?.dismiss();
      witnessing?.begin();
    }

    if (controls.takeExport()) {
      exportSession();
    }

    // Once per frame rather than once per step, unlike the dig: an interaction
    // submits no command and takes effect in no tick, so tying it to the fixed
    // step would only make it later.
    if (controls.takeInteract()) {
      witnessing?.interact();
    }

    // Drained every frame whether or not it is dark, so a press made in
    // daylight is answered rather than held until nightfall.
    if (controls.takeCamp()) {
      makeCamp();
    }

    // Camp's sibling on the same crossing, drained beside it and for the same
    // reason: it queues no command of its own and a press between two frames
    // would otherwise be lost.
    if (controls.takeWait()) {
      waitAWhile();
    }

    // And the third of the family, drained beside them because it is the same
    // kind of press: no command of its own, and a key that arrived between two
    // frames would be lost if it were sampled instead of latched. **It is
    // drained while the dark is up as well**, which the other two are not — that
    // is the option the ruling added, and the one key that still means something
    // to a body that has gone under ({@link lieDown}).
    if (controls.takeRest()) {
      lieDown();
    }

    // Captured before the toggles land, because what the pointer lock does
    // next is a fact about the *transition*: a panel that opened this frame
    // borrows the mouse, one that closed returns it, and one that merely stayed
    // open must not fight the player over a click they aimed at the world.
    const wasReading = hud.reading;

    for (let toggle = controls.takeJournalToggles(); toggle > 0; toggle -= 1) {
      witnessing?.toggleJournal();
    }

    // The map, on the same latch and the same handshake as the journal: it is
    // the other instrument over what the player honestly holds, and it is bound
    // in every session rather than behind `?dev=1` (Phase 5 decision 10).
    for (let toggle = controls.takeMapToggles(); toggle > 0; toggle -= 1) {
      witnessing?.toggleMap();
    }

    // And the third of them: what is in the player's hands (Phase 7, wave 1F).
    // Same latch, same handshake, same family — one is what they were told, one
    // is where they have been, one is what they are carrying.
    for (let toggle = controls.takeInventoryToggles(); toggle > 0; toggle -= 1) {
      witnessing?.toggleInventory();
    }

    // And the screen those three are pages of (chunk U-3). `Tab` on a keyboard
    // and the pad's *menu* button under a thumb come back out of one door, and
    // what it does is what a system screen's key does everywhere: away if it is
    // up, back to the page the player left it on if it is not. Which page that
    // is belongs to the screen; who fills it is the handler registered above.
    for (let toggle = controls.takeMenuToggles(); toggle > 0; toggle -= 1) {
      hud.menu.toggle();
    }

    /*
     * The hotbar (chunk U-5), drained here because a slot press is a key press
     * like every other one on this list — `docs/decisions/input.md` §5's rule,
     * one layer up: the digits and the page's own tappable slots write the same
     * queue, so there is one path in and one drain out.
     *
     * Two steps and not one, because the two halves belong to different owners.
     * Which handle a slot holds is the **client's** arrangement (`Hotbar`), and
     * what a press means is the **simulation's** door: one `Command::Wield`,
     * refused in silence for a handle the player no longer holds
     * (`docs/decisions/player.md` §24.3). An empty slot queues nothing.
     */
    for (const slot of controls.takeHotbarPresses()) {
      hud.hotbar.press(slot);
    }

    for (const item of hud.hotbar.takeWields()) {
      world.wield(item);
    }

    /*
     * And the inventory page's own two rows, drained beside it because they are
     * the same two doors reached a second way — the strip is *this, under a key*
     * and a row is *this one, by name*. `wear` goes through its **named** form
     * (chunk S-2), which is the difference between putting on the piece the
     * player pressed and putting on whatever is best by the time the tick runs;
     * `P` still sends the bare verb and still means the world's own pick.
     */
    for (const door of hud.inventory.takeDoors()) {
      if (door.verb === "wield") {
        world.wield(door.item);
      } else {
        world.wear(door.item);
      }
    }

    /*
     * And which slot is lit, asked of the simulation every frame while there is
     * anything to light.
     *
     * **The read is guarded and the write is guarded separately.** A wield
     * queued this frame lands on a later tick, so there is nothing to ask but
     * *what is in your hand now* and nothing to ask it of but the world; the
     * `arranged` gate keeps that off the frame of every session nobody has ever
     * pressed a digit in, which is nearly all of them, and `Hotbar.lit` writes
     * only when the answer moved (invariant 8).
     *
     * It is the simulation's word and never the client's memory of a press —
     * `carried()[0]` is whatever happens to be first and this is what the player
     * chose, which is the whole reason `wielded()` is its own read
     * (`world/types.ts`).
     */
    if (hud.hotbar.arranged) {
      hud.hotbar.lit(witnessSource?.wielded() ?? null);
    }

    // Drained whether or not anything listens, so the count does not survive
    // into a session that does. What listens is bound below, in development and
    // behind `?dev=1`: the trace view shows the chronicler's sentences rather
    // than anybody's, and a playtester who fat-fingers T must not meet the log
    // (`hud/panels.ts`, Phase 4 decision 7).
    const traceToggles = controls.takeTraceToggles();

    if (dev) {
      for (let toggle = traceToggles; toggle > 0; toggle -= 1) {
        witnessing?.toggleTrace();
      }
    }

    // The debug window, on the same latch and drained the same way. Its gate is
    // the link alone rather than the build flag (see `watching` above), and it
    // is deliberately **not** a reading panel: it does not join `hud.reading`,
    // so opening it never takes the pointer lock away from a player who is
    // watching for the thing they opened it to see (`dev/debugpanel.ts`).
    const debugToggles = controls.takeDebugToggles();

    for (let toggle = debugToggles; toggle > 0; toggle -= 1) {
      debugPanel?.toggle();
    }

    refreshDebug(frameSeconds);

    // A reading panel wants the mouse as a cursor and a wheel, not as a camera:
    // opening one hands the pointer lock back so the panel can be scrolled, and
    // closing it takes the lock back without a click — the J that closed it is
    // gesture enough. Only when the panel is what freed the mouse, though: a
    // player who opened the journal from the unlocked prompt never lent it
    // anything, and is not grabbed on close. They get the prompt back instead.
    const reading = hud.reading;

    if (reading && !wasReading) {
      if (controls.locked) {
        readingTookLock = true;
        controls.releaseLock();
      } else {
        hud.setOverlay(null);
      }
    }

    if (!reading && wasReading) {
      if (readingTookLock) {
        readingTookLock = false;
        controls.requestLock();
      } else if (!controls.locked) {
        hud.setOverlay(playPrompt);
      }
    }
  },

  step(stepSeconds: number): void {
    // **The world stands still at a death screen** ({@link mourning}): the
    // player has nothing to act with and a town going about its business behind
    // a menu would be hours passing that nobody spent.
    if (mourning) {
      return;
    }

    // **The sim's verdict about the player's legs, applied to the host's own
    // walk** (`sim_core::Afflictions::OFF_ITS_FEET`, and
    // {@link CRAWL_INTENT_SCALE}'s note). The player's gait has always been host
    // physics — the simulation has never moved their body — so what crosses is
    // a bit and what this does with it is presentation. Nothing here decides how
    // hurt anybody is.
    //
    // A player who has been knocked out is not walking at all, and the world is
    // being burned through above rather than stepped here.
    const intent = controls.takeIntent();
    const crawling = witnessing?.offItsFeet ?? false;

    // **The developer's flight, which is the walk with the ground taken out**
    // (chunk Fx-3, `dev/flight.ts`). The toggle is drained here rather than in
    // `render` because it changes what a *step* does, and `flight` is `null` in
    // every session that did not ask for it — so a production build drains
    // nothing and takes the branch it always took.
    if (flight !== null && controls.takeFlyToggles() % 2 === 1) {
      // The sentence comes off the tool rather than out of this file, which is
      // what keeps `dev/flight.ts` out of a production bundle: `main.ts` holds
      // only a *type* import of it, and the module itself arrives through the
      // dynamic import above.
      hud.setNotice(flight.notice(flight.toggle(player)));
    }

    if (flight?.flying === true) {
      // No crawl scale and no ground: a flying developer is not walking, so the
      // simulation's verdict about their legs has nothing to apply to. Every
      // other line of this step is unchanged — the interest below still names
      // the tile they are over, so the world streams under a flight exactly as
      // it does under a walk.
      flight.step(stepSeconds, player, intent, controls.flyLift);
    } else {
      player.step(
        stepSeconds,
        crawling
          ? {
              ...intent,
              forward: intent.forward * CRAWL_INTENT_SCALE,
              strafe: intent.strafe * CRAWL_INTENT_SCALE,
              sprint: false,
            }
          : intent,
      );
    }

    // **Where the player is standing, when it is worth telling** (plan W11).
    // Offered every step and sent only while somebody is engaged with or
    // answering the player's figure, which is the witnessing layer's read of
    // the simulation's own state ({@link Witnessing.standIfWatched}) — and
    // deduped below that, so a fight the player stands still through is one
    // command rather than one a tick.
    witnessing?.standIfWatched(player.tile);

    // The on-screen strike button, which a thumb presses directly: a touch
    // session has no crosshair to disambiguate the dig with, so it gets the
    // verb as a button of its own (`input/controls.ts`'s `TOUCH_VERBS`).
    if (controls.takeStrike()) {
      witnessing?.strike();
    }

    // The bow, which is aimed like the dig and cited like the strike: the
    // witnessing layer picks the body, refuses out loud for anything the world
    // would refuse in silence, and hands back **where the arrow flies** — or
    // `null`, which is a press that queued nothing and therefore draws nothing
    // (`witness/witness.ts`'s `loose`).
    if (controls.takeLoose()) {
      const at = witnessing?.loose() ?? null;

      if (at !== null) {
        arrows.launch(player.position, at);
      }
    }

    // And the hands panel's own two. They name nothing, aim at nothing and are
    // pressed standing still: which piece goes on is the simulation's pick.
    if (controls.takeWear()) {
      witnessing?.wear();
    }

    if (controls.takeDoff()) {
      witnessing?.doff();
    }

    if (controls.takeDig()) {
      // **The mouse-0 rule, asked once** (wave 2D-a, and chunk Fx-2's third
      // answer). The left button has meant *dig* since Phase 1, means *swing*
      // whenever the strike pick has somebody in it, and since the owner's
      // ruling of 2026-08-31 means **nothing at all** while a person with no
      // quarrel fills the crosshair — the rule and the sentence a player reads
      // it off are both the witnessing layer's (`Witnessing.mousePress`, and the
      // prompt beside it), asked here rather than decided here so the line under
      // the crosshair and the press cannot come to different conclusions.
      switch (witnessing?.mousePress ?? "dig") {
        case "strike":
          witnessing?.strike();

          break;

        case "nothing":
          // **Ruled option 2**: the quarrel gate stands and the offer is empty,
          // so nothing is sent and nothing is dug through the person. The prompt
          // has been saying so for as long as the crosshair has been on them;
          // this says it where every other refusal is written, because a button
          // that quietly does nothing is the thing the graybox ruling is about.
          witnessing?.noQuarrel();

          break;

        default: {
          // Aimed from the pose *now* rather than from the one the last frame
          // drew: a dig is submitted to the next tick, so it should describe where
          // the player is looking at the moment they asked, not a frame ago.
          const hit = traceCrosshair(1);

          if (hit === null) {
            // The ray left the world without meeting anything: there is no tile to
            // name, which is the one refusal the witnessing layer cannot be told
            // about by being handed one (wave 1.5's ruling — every refused action
            // says why).
            witnessing?.missed("dig");
          } else {
            // A command, not an edit. Whether the tile can be dug at all is the
            // simulation's decision, and it answers with an event
            // (`world/wasm.ts`).
            world.dig(hit.tile);
          }

          break;
        }
      }
    }

    // The gather verb takes the dig's path exactly, and that is the point:
    // what it claims is work done *to* a tile, so it names one through the
    // crosshair and lets the simulation decide what kind of work is there
    // (`witness/witness.ts`'s `gather`). Aimed from the pose now, for the dig's
    // reason.
    if (controls.takeGather()) {
      const hit = traceCrosshair(1);

      if (hit === null) {
        witnessing?.missed("gather");
      } else {
        witnessing?.gather(hit.tile);
      }
    }

    // And the drop, which names the tile the player is *standing on* rather
    // than the one under the crosshair: putting something down is a thing you do
    // where you are, and aiming it would make it a shot.
    if (controls.takeDrop()) {
      witnessing?.dropCarried(player.tile);
    }

    // The player's tile is the only thing the client tells the world about
    // itself. **Offered every tick and sent when it changes**, which since item 6
    // is the whole contract rather than an optimisation: the host reports
    // interest when it changes, the simulation *holds* it, and coverage is
    // evaluated every tick from what is held — so a repeat of what is already
    // held is not news (`world/wasm.ts`'s `submitInterest`). What used to be
    // written here — "the world ignores a repeat" — was true of terrain and
    // false of promotion, and standing still for twenty-five seconds demoted the
    // town under the player's feet.
    world.setInterest(player.tile, INTEREST_RADIUS_CHUNKS);
    world.step();

    // The roads and the drawn positions move on the fixed step too, so that a
    // slow frame does not teleport anybody. None of it reaches the simulation
    // (plan decision 8): it is presentation stepped on the sim's clock. It is
    // also where the day boundary is noticed — after `world.step`, so the tick
    // that turned the calendar is the tick the witnessing re-reads on
    // (`witness/witness.ts`).
    witnessing?.step(stepSeconds);
  },

  render(alpha: number, frameSeconds: number): void {
    // Drained once per frame rather than once per step, so several ticks' worth
    // of new chunks coalesce into one upload pass with one budget.
    chunks.apply(world.takeMeshUpdates());

    target = traceCrosshair(alpha);

    if (target === null) {
      highlight.hide();
    } else {
      highlight.show(target.tile);
    }

    const pose = player.pose(alpha);

    // After the meshes, so a body standing on a chunk that arrived this frame is
    // placed on it rather than under it.
    witnessing?.render(player.position, pose.yaw);

    // And the on-screen interact button says what the prompt says, because the
    // prompt on a touchscreen has no key in it to name (`input/touch.ts`). Read
    // here rather than pushed from the witnessing layer for the reason the key
    // card's dismissal is read here: it is a fact about the frame that was just
    // composed, and the button writes nothing when the word has not moved.
    // `null` on a desktop, where the whole call is one optional chain.
    //
    // **The verb comes back with its crime word on it** since chunk Fx-2
    // (`Witnessing.aimedVerb`): a thumb pressing *pick up* is owed the same
    // warning a finger on `F` gets, and one composition serves both surfaces so
    // they cannot come to say different things.
    touchControls?.aimAt(witnessing?.aimedVerb ?? null);

    // The sky, before the frame is drawn against it. The preview control is
    // stepped first so a sweep in progress is the phase this frame uses rather
    // than the last one's (`dev/daynight.ts`); `MORNING_PHASE` is what a client
    // that does not know the time shows, and it is a fixed light rather than a
    // guess that would have to be corrected (`render/daylight.ts`).
    // The shaft in the air, on frame time rather than on the fixed step: it is
    // presentation, it must not stutter when the step does, and it changes
    // nothing a replay can see.
    arrows.update(frameSeconds);

    daylight?.step(frameSeconds);
    view.setTimeOfDay(timeOfDay() ?? MORNING_PHASE);

    // The pass-time verbs' own two lines of presentation: which of them is on
    // offer, and how dark the screen is while it runs.
    //
    // One line for the three, and the order is what each offer is *about*. The
    // rest verb goes first because it is the only one offered by the **body**:
    // being hurt enough to lie up is rare, it is news, and a player who is
    // reading *badly hurt · your sight is greying* at the bottom of the frame is
    // owed the verb that answers it. Camp beats the wait for its own reason —
    // after dark, waiting out the night is what a player means by waiting — and
    // three prompts in one slot would be the night taking a line off a person
    // the player had just walked up to (`hud/hud.ts`'s prompt slots).
    hud.camp.set(
      canRest() ? REST_PROMPT : canCamp() ? CAMP_PROMPT : canWait() ? WAIT_PROMPT : null,
    );
    stepCamp(frameSeconds);

    view.resize();
    view.setCamera(pose);
    view.render();

    // And the mark for whatever the debug window is following, over the frame
    // that was just drawn: one projection, and nothing at all when nothing is
    // tracked (`dev/track.ts`).
    followTracked(pose);

    const stats = world.stats();
    hud.update(
      {
        fps: loop.stats.fps,
        position: player.position,
        standing: player.standing,
        chunkCount: chunks.size,
        columnCount: chunks.columnCount,
        pendingUploads: chunks.pendingUploads,
        tick: stats.tick,
        clampedFrames: loop.stats.clampedFrames,
        droppedSteps: loop.stats.droppedSteps,
        worldHash: stats.worldHash,
        dugTiles: stats.dugTiles,
        generatedChunks: stats.generatedChunks,
        drawCalls: view.drawCalls,
        // The world's own clock, read off the witnessing surface — which reads
        // it off the tick summary rather than the boundary (`world/wasm.ts`).
        // `null` in a world with no history, which is a world whose calendar
        // nobody is looking at.
        date: witnessSource?.date() ?? null,
      },
      frameSeconds,
    );
  },
});

loop.start();

/**
 * Keeps the debug window's clock, drains the simulation's notes and redraws it.
 *
 * Called once per frame from `beginFrame` and it does almost nothing: the clock
 * is two assignments (so an entry written during this frame's `step` carries the
 * right stamp), and everything else is behind a one-second gate
 * (`dev/debuglog.ts`'s `DEBUG_REFRESH_SECONDS`). A session that never asked for
 * the window returns on the first line.
 *
 * The drain is a poll rather than a push because the simulation has no channel
 * to push down: a note is made inside a tick and lives in an unhashed ring until
 * somebody reads it, and how many went past unread is exactly what
 * `takeSimNotes` works out (`sim_core::local`'s `Trail`).
 */
/**
 * Draws the mark for whatever the debug window is tracking, once a frame.
 *
 * **Three reads and no fourth.** Where a tracked body is being drawn comes off
 * the witnessing layer's own presence list through the walk smoothing
 * (`witness/witness.ts`'s `drawnAt`) — the same list the crowd is drawn from, so
 * this adds no boundary crossing and the mark sits over the body the player can
 * see rather than over the tile the simulation last reported. The lens is the
 * camera's own field of view and aspect, read off the camera rather than
 * restated, because a mark that disagreed with the frame under it would be worse
 * than no mark. The rectangle it is clamped into is the HUD layer's own box, and
 * the element that lives in that layer is what asks for it
 * (`dev/trackmarker.ts`).
 *
 * A place needs none of that: it does not move, so its own tile is the answer
 * and the drawn position handed over is `null`.
 */
/**
 * The mark as it was last drawn, flattened for a driver (`dev/handle.ts`).
 *
 * Read off the element rather than re-projected here, on the rule the window's
 * own lines are read off the panel: what a script asserts on is what was drawn,
 * not a second computation of it.
 */
function trackedMark(): DebugMarker | null {
  const shown = trackMarker?.reading ?? null;

  return shown === null
    ? null
    : {
        onScreen: shown.mark.onScreen,
        x: shown.mark.x,
        y: shown.mark.y,
        angle: shown.mark.angle,
        label: shown.label,
      };
}

function followTracked(pose: CameraPose): void {
  if (trackMarker === null) {
    return;
  }

  const target = debugPanel?.tracked ?? null;
  const drawn = target?.kind === "body" ? (witnessing?.drawnAt(target.figure) ?? null) : null;
  const lens = { fovDegrees: view.camera.fov, aspect: view.camera.aspect };

  trackMarker.follow(target, pose, lens, drawn);
}

function refreshDebug(frameSeconds: number): void {
  if (debugLog === null) {
    return;
  }

  const stats = world.stats();

  debugSeconds += frameSeconds;
  debugLog.clock(debugSeconds, stats.tick);
  sinceDebugRefresh += frameSeconds;

  if (sinceDebugRefresh < debugRefreshSeconds) {
    return;
  }

  sinceDebugRefresh = 0;

  if (debugWatch?.localNotesMade !== undefined && debugWatch.localNotesSince !== undefined) {
    const made = debugWatch.localNotesMade();

    if (made > debugLog.simSeen) {
      debugLog.takeSimNotes(made, debugWatch.localNotesSince(debugLog.simSeen));
    }
  }

  // The two lists are handed over as *thunks* rather than as rows: the panel
  // calls one only when the tab that draws it is on screen, so a window that is
  // shut or open on Info pays a closure and nothing else
  // (`dev/debugpanel.ts`'s `NearReading`).
  debugPanel?.update(debugReading(stats), debugLog, {
    bodies: () => witnessing?.watchedBodies ?? [],
    places: () => witnessing?.watchedPlaces ?? [],
    orders: () => witnessing?.watchedOrders ?? [],
  });
}

/**
 * Gathers everything the Info tab shows, from the four places that hold it.
 *
 * Assembled here rather than anywhere else because this is the only file that
 * can see all four — the player controller, the witnessing layer, the world
 * handle and the simulation's own debug reads — and because a *value* is what
 * `infoLines` renders and what a self-check can build by hand
 * (`dev/debuglog.ts`).
 *
 * Nothing here decides anything and nothing is derived that the sources did not
 * already answer: where a source has no answer the field is `null`, and the
 * window says so in words.
 */
function debugReading(stats: WorldStats): DebugReading {
  const watched = witnessing?.watched ?? null;
  const tile = player.tile;
  const chunk = chunkOfTile(tile.x, tile.y, tile.z);

  return {
    position: player.position,
    tile,
    chunk: { cx: chunk.cx, cy: chunk.cy },
    // Where the view points, which the Bodies and Places lists measure their
    // bearings against. Read off the same pose the camera uses; yaw is not
    // interpolated, so the fraction asked for does not matter
    // (`player/controller.ts`'s `pose`).
    facing: player.pose(0).yaw,
    date: witnessSource?.date() ?? null,
    tick: stats.tick,
    phase: watched?.phase ?? null,
    figure: witnessing?.figure ?? null,
    figureName: witnessing?.introduced ?? "",
    here: watched?.here ?? null,
    hereName: watched?.hereName ?? "",
    hereDistance: watched?.hereDistance ?? null,
    promoted: watched?.promoted ?? null,
    promotedName: watched?.promotedName ?? "",
    uncovered: debugWatch?.localUncoveredTicks?.() ?? null,
    demotionTicks: debugWatch?.localDemotionTicks?.() ?? null,
    roster: watched?.roster ?? 0,
    stages: watched?.stages ?? [],
    presence: watched?.presence ?? 0,
    props: watched?.props ?? 0,
    drawnBodies: watched?.bodies ?? 0,
    jobs: watched?.jobs ?? 0,
    jobsTaken: watched?.jobsTaken ?? 0,
    lots: watched?.lots ?? 0,
    piles: watched?.piles ?? 0,
    aimed: watched?.aimed ?? null,
    tierSwaps: watched?.tierSwaps ?? [],
    snaps: watched?.snaps ?? 0,
    worldHash: stats.worldHash,
    droppedSteps: loop.stats.droppedSteps,
  };
}

/**
 * Waits until whatever was just written to the HUD is on the glass.
 *
 * Two frames, because a callback runs *before* the paint it was scheduled for,
 * so the second one is the first moment the pixels are known to exist. Thirty
 * milliseconds, and the difference between a loading line that always shows and
 * one that shows whenever the wait happens to be long enough — which is the
 * shape of both waits this file has: the bootstrap behind *begin*, and the
 * re-run behind a loaded file.
 */
/** Whatever the load door built, if it was opened. See where it is read. */
function loadedSession(): RestoredWorld | null {
  return loaded;
}

async function painted(): Promise<void> {
  await new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve));
  });
}

/** Returns the tile under the crosshair at an interpolation fraction, or `null`. */
function traceCrosshair(alpha: number): RayHit | null {
  const { origin, direction } = rayFromPose(player.pose(alpha));

  return raycastVoxels(origin, direction, DIG_RANGE_TILES, world.terrain);
}

/**
 * Saves the session as a replay file.
 *
 * The whole of the client's involvement: ask the world for the text, hand it to
 * the browser. What makes the file interesting happens elsewhere —
 * `cargo run -p sim-cli -- verify <file>` re-runs the commands against a fresh
 * simulation and compares every hash (README, "Recording a session").
 */
function exportSession(): void {
  const { tick } = world.stats();
  const text = world.exportReplay();
  const ticks = `${tick} tick${tick === 1 ? "" : "s"}`;

  downloadText(`deep-world-session-${tick}-ticks.json`, text);
  hud.setNotice(`replay saved — ${ticks}, ${(text.length / 1024).toFixed(0)} KiB`);
}

if (dev) {
  // Development only *and* asked for, and dynamically imported so the production
  // bundle drops both the checks and everything they pull in. This is the
  // closest thing the client has to a test suite: no framework is worth its
  // weight in a renderer that exists to be thrown away, but the accumulator, the
  // collision sweep, the raycast and the mesh contract are all logic, and logic
  // that nobody checks is logic that is wrong (`src/dev/selfcheck.ts`).
  //
  // Behind `?dev=1` since Phase 4: the checks write their verdict to the HUD
  // when they fail, and a playtester on a dev build is not the audience for it.
  void import("./dev/selfcheck").then(({ runSelfChecks }) => {
    const failures = runSelfChecks();
    document.body.dataset["selfcheck"] = failures.length === 0 ? "ok" : "failed";

    if (failures.length > 0) {
      for (const failure of failures) {
        console.error(`self-check: ${failure}`);
      }

      hud.setNotice(`${failures.length} self-check failure(s) — see the console`);
    }
  });
}

if (dev) {
  // The day/night preview (`dev/daynight.ts`): a slider, a sweep, and a way
  // back to the world's clock. Behind both gates like the self-checks — the
  // production bundle never contains it, and a development session that did not
  // ask never grows a control — and dynamically imported so neither does the
  // module graph.
  //
  // It exists because the thing it previews takes twenty minutes to happen once
  // and cannot start happening until the client has watched a whole day go by,
  // and because how dark night gets is a judgement somebody has to make by
  // looking (Phase 5 decision 8, the art-pass precedent).
  void import("./dev/daynight").then(({ DaylightPreview }) => {
    daylight = new DaylightPreview(hudRoot, worldTime);
  });
}

/**
 * The crossing the debug handle drives, kept apart from the one the keyboard
 * uses.
 *
 * A headless driver and a player pressing C are two callers with no way to see
 * each other, and one crossing between them could be opened by one and closed
 * by the other (`host/fastforward.ts`). Two instances over the same host cost
 * nothing and make that impossible.
 */
const driverForward = new FastForward(fastForwardHost);

if (import.meta.env.DEV) {
  // The handle a headless browser drives the exit-criteria session through
  // (`src/dev/handle.ts`). Development only, and — unlike the checks above —
  // *not* behind `?dev=1`: it draws nothing, says nothing and is invisible to
  // anybody who does not go looking for `window.__deepWorld`, and every
  // screenshot recipe and headless script in this tree poses the camera through
  // it. Gating it would gate the tooling rather than the surfaces the plan asks
  // to keep away from a playtester.
  void import("./dev/handle").then(({ installDebugHandle }) => {
    installDebugHandle({
      world,
      exportSession,
      look: (deltaYaw: number, deltaPitch: number) => {
        player.look(deltaYaw, deltaPitch);
      },
      interact: () => {
        witnessing?.interact();
      },
      toggleJournal: () => {
        witnessing?.toggleJournal();
      },
      toggleTrace: () => {
        witnessing?.toggleTrace();
      },
      toggleDebug: () => {
        debugPanel?.toggle();
      },
      debug: () => ({
        available: debugPanel !== null,
        open: debugPanel?.open ?? false,
        tab: debugPanel?.showing ?? "",
        // Read out of the panel rather than re-composed here, so what a driver
        // asserts on is the same text a reader sees (`dev/debugpanel.ts`).
        info: debugPanel?.linesFor("info") ?? [],
        // Worked out on the ask, which is what a headless read of these two
        // costs and the whole of what it costs (`dev/handle.ts`).
        bodies: debugPanel?.linesFor("bodies") ?? [],
        places: debugPanel?.linesFor("places") ?? [],
        log: debugPanel?.linesFor("log") ?? [],
        // Empty in every session that did not ask for the developer's hands,
        // which is the `?dev=1` gate read back rather than inferred
        // (`dev/give.ts`).
        give: debugPanel?.linesFor("give") ?? [],
        simNotesSeen: debugLog?.simSeen ?? 0,
        // The tracked target's *identity* rather than its row, and the mark as
        // it was last drawn — the two things a screenshot of an arrow at the
        // edge of a frame cannot be asked (`dev/track.ts`).
        tracking: debugPanel?.trackedKey ?? "",
        marker: trackedMark(),
      }),
      track: (tab: "bodies" | "places", row: number) => {
        debugPanel?.track(tab, row);
      },
      // The give tab's own press, and the flight key, both through the surfaces
      // a person uses (chunk Fx-3): a row of the window, and the toggle the F4
      // handler drains. Neither exists in a session that did not ask, and both
      // answer with a shrug there rather than with a throw.
      give: (row: number) => {
        debugPanel?.give(row);
      },
      fly: () => {
        if (flight === null) {
          return false;
        }

        hud.setNotice(flight.notice(flight.toggle(player)));

        return flight.flying;
      },
      toggleMap: () => {
        witnessing?.toggleMap();
      },
      figure: () => ({
        figure: witnessing?.figure ?? null,
        introduced: witnessing?.introduced ?? "",
        carrying: (witnessing?.carrying ?? []).map((entry) => entry.event),
      }),
      opening: () => opened,
      eras: () => {
        const book = witnessing?.eras ?? null;

        // Flattened here rather than in the witnessing layer, because the shape
        // is a *driver's* convenience and the projection is the client's: what
        // is asserted on is the same book the panel draws, read the way a script
        // wants to read it (`dev/handle.ts`'s `DebugEras`).
        return {
          ages: book?.eras.length ?? 0,
          refused: book?.refused ?? 0,
          lines: (book?.eras ?? []).flatMap((era) =>
            era.lines.map((line) => ({
              era: era.ordinal,
              text: line.text,
              cites: line.cites,
            })),
          ),
        };
      },
      origins: () => ({
        read: born.read,
        // What the start menus settled on, whichever card asked: the born
        // session's name and the declining session's are the same field, because
        // downstream there is one name (`hud/origins.ts`). Empty is a session
        // that skipped, which is the fact the spawn claim is gated on and
        // therefore the one a check most needs to be able to read.
        called,
        // The offer as the screen would have drawn it, flattened for a script —
        // `dev/handle.ts`'s `DebugEras` makes the same trade for the same reason:
        // the shape is a *driver's* convenience and the projection is the
        // client's, so what is asserted on is the same list the panel drew.
        cards: offer.cards.map((card) => ({
          kind: card.kind,
          line: card.line,
          cites: card.cites,
          claims: card.claims.map((claim) => ({ line: claim.line, cites: claim.cites })),
        })),
        refused: offer.refused,
        chosen:
          born.chosen === null
            ? null
            : { kind: born.chosen.kind, line: born.chosen.line, name: born.name },
      }),
      town: () => ({
        promoted: witnessSource?.promoted() ?? null,
        board: (witnessing?.workBoard ?? []).map((post) => ({
          kind: post.kind,
          x: post.site.x,
          y: post.site.y,
          z: post.site.z,
          worked: post.worker !== null,
        })),
        bodies: witnessing?.present.length ?? 0,
        lots: (witnessing?.buildings ?? []).map((lot) => ({
          kind: lot.kind,
          name: witnessSource?.lotName(lot.kind) ?? "",
          trade: lot.trade,
          workX: lot.work.x,
          workY: lot.work.y,
        })),
        carried: (witnessing?.carriedItems ?? []).map((entry) =>
          witnessing?.labelOf(entry.item) ?? "",
        ),
        // Every named thing the world holds, in the simulation's own order
        // (chunk 2C): the handful of objects something record-worthy has
        // touched, which is what a playtest hunting a famous blade is looking
        // for. Flattened for a driver on `cards`' terms — the shape is a
        // driver's convenience and the projection is the client's.
        relics: (witnessing?.namedThings ?? []).map((thing) => ({
          item: thing.item,
          words: thing.words,
          history: witnessing?.relicOf(thing.item) ?? [],
        })),
        piles: witnessing?.watchedTiles ?? 0,
      }),
      // The arrest chain at one instant, straight off the witnessing layer's own
      // reading ({@link Witnessing.watching}). Nothing is composed here: a
      // driver asserting on it is asserting on what this client decided. Taken
      // **once** per call — the reading crosses the wall for the guards standing
      // near the player, and six reads of it would be six walks of them.
      watch: () => {
        const reading = witnessing?.watching ?? null;

        return {
          wanted: reading?.wanted ?? "",
          guardAway: reading?.guardAway ?? -1,
          standing: reading?.standing ?? false,
          stood: reading?.stood ?? null,
          arresting: reading?.arresting ?? -1,
          beat: reading?.beat ?? "",
          // And what the aiming prompt is offering, which is the ruling's other
          // half: a driver walking up to a heap can read whether taking it would
          // be a crime before it presses.
          aimedVerb: witnessing?.aimedVerb ?? "",
        };
      },
      gather: () => {
        const hit = traceCrosshair(1);

        if (hit === null) {
          return null;
        }

        witnessing?.gather(hit.tile);

        return hit.tile;
      },
      drop: () => {
        witnessing?.dropCarried(player.tile);
      },
      toggleInventory: () => {
        witnessing?.toggleInventory();
      },
      wait: () => {
        waitAWhile();
      },
      books: () =>
        (witnessing?.shelf ?? []).map((book) => ({
          id: book.id,
          author: book.author,
          authorName: book.authorName,
          entries: book.entries,
          x: book.position.x,
          y: book.position.y,
        })),
      ruin: () => {
        const ruin = witnessing?.ruinHere ?? null;

        return ruin === null ? null : { sack: ruin.sack, since: ruin.since };
      },
      map: () => {
        const survey = witnessing?.survey() ?? null;
        const walked = witnessing?.walked ?? null;

        // Zeroes rather than a null shape, so a driver in a world with nothing
        // to witness reads "nothing surveyed" instead of having to branch.
        return {
          walkedCells: walked?.walkedCount ?? 0,
          seenCells: walked?.seenCount ?? 0,
          full: walked?.full ?? false,
          refused: walked?.refused ?? { cells: 0, marks: 0 },
          places: survey?.places.map(({ name, x, y, knowledge }) => ({ name, x, y, knowledge })) ?? [],
          heard: survey?.heard ?? [],
          marks: survey?.marks.length ?? 0,
          roadStrokes: survey?.roads.length ?? 0,
          trailPoints: survey?.trail.length ?? 0,
        };
      },
      setOverlay: (message: string | null) => {
        hud.setOverlay(message);
      },
      dismissKeys: () => {
        hud.keys?.dismiss();
      },
      vantages: () =>
        witnessing?.vantages().map(({ id, label, x, y }) => ({ id, label, x, y })) ?? [],
      view: (id: string) => {
        const vantage = witnessing?.vantage(id) ?? null;

        if (vantage === null) {
          return false;
        }

        // **A vantage may not bury the player** (chunk Fx-7, closing Fx-3's
        // filing). The vantage is already pulled in along its own ray until a
        // body fits and hands the full stand-off back when nothing along it did
        // (`witness/witness.ts`), which is the right answer for a camera and the
        // wrong one for a body — so the last word is here, at the one call that
        // writes the transform with no physics under it.
        const room = standingRoomNear(vantage, world.terrain, (x, y) =>
          surfaceZ(world.terrain, x, y),
        );

        if (room === null) {
          // Refused rather than teleported into rock. A picture that cannot be
          // taken from the ground is a thing the driver has to know about, and a
          // `false` is what every other unreachable subject already answers.
          hud.setNotice(`nowhere to stand to look at ${vantage.label} — it is walled in`);

          return false;
        }

        if (room.x !== vantage.x || room.y !== vantage.y || room.z !== vantage.z) {
          // Said out loud, for `?home=`'s reason one boot sequence up: a shot
          // taken from somewhere other than the vantage that was asked for is a
          // shot of something slightly else, and a session that moved quietly is
          // an unreproducible one.
          hud.setNotice(`stood clear of ${vantage.label} — the vantage would have buried you`);
        }

        player.placeAt({ x: room.x, y: room.y, z: room.z }, vantage.yaw, vantage.pitch);
        // The world is told where the player is on the next step like any other
        // frame; nothing here reaches past the seam.
        return true;
      },
      journal: () => journal.entries,
      people: () => people.everyone,
      staged: () =>
        witnessing?.staged.map((delivery) => ({
          id: delivery.beat.id,
          channel: delivery.beat.channel,
          where: delivery.where,
          when: delivery.when,
          earliest: delivery.beat.earliest,
          refs: delivery.beat.refs,
        })) ?? [],
      runDays: (days: number) => {
        // The host's own step, without a frame between the ticks: the world is
        // advanced and the witnessing is stepped in the same order the loop
        // does it, so every day boundary is noticed in place. The client owns
        // no calendar arithmetic, so a day is "until the *date* turns" and
        // never a count (`host/fastforward.ts`).
        //
        // Unbudgeted, which is the one thing that separates it from the skip: a
        // driver has no frame to keep and asked for the whole thing.
        for (let day = 0; day < days; day += 1) {
          if (!driverForward.start()) {
            break;
          }

          if (driverForward.advance(Number.POSITIVE_INFINITY) !== "morning") {
            break;
          }
        }

        return witnessSource?.date() ?? null;
      },
      runSeconds: (seconds: number) => {
        runStepped(fastForwardHost, seconds);

        return witnessSource?.date() ?? null;
      },
      trace: (event: number) => witnessSource?.describe(event) ?? "",
      stats: () => {
        // The pose at the end of the tick — alpha 1 — because a driver aiming a
        // screenshot wants where the view *is*, not where a frame interpolated
        // it to.
        const pose = player.pose(1);

        return {
          fps: loop.stats.fps,
          drawCalls: view.drawCalls,
          triangles: view.triangles,
          chunkMeshes: chunks.size,
          columns: chunks.columnCount,
          pendingUploads: chunks.pendingUploads,
          droppedSteps: loop.stats.droppedSteps,
          position: player.position,
          yaw: pose.yaw,
          pitch: pose.pitch,
          target: target?.tile ?? null,
          props: witnessing?.drawObjects ?? 0,
          aimedAt: witnessing?.aimedAt?.label ?? null,
          aimedId: witnessing?.aimedAt?.id ?? null,
          journalEntries: journal.size,
          peopleMet: people.size,
          // What the town is reacting to right now, so a driver can photograph
          // the staged sequence while it is running instead of counting
          // seconds (`witness/director.ts` decides when it starts).
          staging:
            witnessing?.reacting == null
              ? null
              : { beat: witnessing.reacting.id, refs: witnessing.reacting.refs },
          // The world's date, so a headless driver can say how long it waited
          // for a day boundary — the cadence everything witnessable is pulled
          // on (`witness/witness.ts`). `null` without a witnessing source.
          date: witnessSource?.date() ?? null,
          ...world.stats(),
        };
      },
    });
  });
}
